/**
 * JobApplicationWizard.jsx — RC 1.41 Phase D
 *
 * Forward-only 4-step modal-style wizard owned by the job detail
 * page. Steps: Job (+ optional Analyse Fit) → Choose CV → Cover
 * Letter → Review/Name. Creates the application pack on Step 4.
 *
 * Hard rules (RC 1.41 product contract):
 *   • Job context is set in Step 1 and reused everywhere; no JD
 *     re-entry.
 *   • RC 1.44.H4 — Recommended-CV surface retired. Step 2 offers
 *     ONLY two paths: use the Source CV (free) or generate a fresh
 *     tailored CV for THIS job (paid `cv_improve`, charged only on
 *     strict keyword improvement vs Source CV). No cross-job variant
 *     reuse, no recommendation list. Existing variants remain visible
 *     in /documents as historical artifacts.
 *   • Generate flow auto-runs Analyse Fit on the new variant, then
 *     calls finalize-charge; tailoring (`cv_improve`) is consumed
 *     only when the new CV beats the Source CV on BOTH axes.
 *     Fallback C: missing baseline → tailoring waived.
 *   • Cover letter is optional. "Skip cover letter" = tertiary link.
 *     Generated letter is editable in Step 3 only; locks once Step 4
 *     renders.
 *   • Pack documents become immutable after creation (Phase B
 *     contract; nothing in this wizard tries to mutate them).
 *   • No splash. SessionStorage-backed state survives refresh.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, Sparkles,
  Mail, FileText, Eye, Lock, AlertCircle, Skull,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { withTimeoutRecovery, findExistingArtefact } from '@/lib/timeoutRecovery';
// RC 1.52 Phase 3A.DUP — shared, collision-proof title-stamp helpers.
import { mintCvVariantTitle, mintCoverLetterTitle } from '@/lib/titleStamps';
// RC 1.52 Phase 3A.4 — UX cooldown after successful Generate
// dispatch so the CV button can't be re-clicked on a stale balance.
import {
  startCooldown, cvVariantCooldownKey,
  analyseFitCooldownKey, useCooldown,
} from '@/lib/actionCooldown';
import {
  writeInflightCvMarker, clearInflightCvMarker,
  readInflightCvMarker, isInflightMarkerExpired,
  findVariantByTitleStamp,
} from '@/lib/inflightCvRecovery';
import {
  writeInflightLetterMarker, clearInflightLetterMarker,
  readInflightLetterMarker, isInflightLetterMarkerExpired,
  findLetterByTitleStamp,
} from '@/lib/inflightLetterRecovery';
import {
  writeInflightFitMarker, clearInflightFitMarker,
  readInflightFitMarker,
} from '@/lib/inflightAnalyseFitMarker';
import {
  getOrMintIdempotencyKey, clearIdempotencyKey,
} from '@/lib/idempotencyKeys';
// RC 1.57 Generation Session Guard — see lib/generationSession.js
// for full rationale. Persists an Analyse-Fit session across
// tab-switches / remounts so a stray re-dispatch can never mint
// a fresh chargeable lifecycle.
import {
  getOrMintSession,
  markTerminalSuccess,
  markHydrationStable,
  releaseSessionIfStable,
} from '@/lib/generationSession';
import { invalidate as cacheInvalidate } from '@/lib/listCache';
import { runAnalyzeFitWithPolling } from '@/lib/analyseFitPolling';
import { useRotatingProgress, humaniseWizardError } from '@/lib/wizardProgress';
import CvRenderer from '@/components/render/CvRenderer';
import AnalyseFitExplainer from '@/components/render/AnalyseFitExplainer';
import UsageExhaustedModal from '@/components/UsageExhaustedModal';
import InferiorSourceModal from '@/components/InferiorSourceModal';
import { BaselineRefreshModal } from '@/components/BaselineRefreshModal';
import GenerateCvOptionsSheet, { TEMPLATES, TONES, LENGTHS, DEFAULTS as GEN_CV_DEFAULTS } from '@/components/wizard/GenerateCvOptionsSheet';
import FinalPackConfirmModal from '@/components/wizard/FinalPackConfirmModal';
import OptimisationDefenseCards from '@/components/wizard/OptimisationDefenseCards';
import Step3ScoreLandscape from '@/components/wizard/Step3ScoreLandscape';
import ScoreVariancePill from '@/components/ScoreVariancePill';
import { useCredits, getActionCost } from '@/lib/creditsContext';
import { handleCreditsError } from '@/lib/handleCreditsError';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';

// RC 1.45.PLAN-EVENTS — fire `dashboard_first_action` exactly ONCE
// per session, the first time the user runs any of the four paid
// actions. Tracks "did they reach the value moment in this session?"
// — the single most predictive engagement signal. Uses sessionStorage
// (not localStorage) so it resets on each fresh login session.
function fireFirstActionOnce(actionType) {
  try {
    const k = 'ndor_dashboard_first_action_fired';
    if (sessionStorage.getItem(k) === '1') return;
    track('dashboard_first_action', { action_type: actionType });
    sessionStorage.setItem(k, '1');
  } catch { /* never throw from telemetry */ }
}
import {
  loadWizardState, saveWizardState, clearWizardState, defaultPackName,
} from '@/lib/wizardState';

// RC 1.49.2 — restructured 4-step linear flow. The legacy "Job"
// context step is removed (the user already arrives from the job
// page) and Analyse Fit is promoted to its own dedicated step
// AFTER the cover letter, instead of being embedded inside the
// letter step where it competed with the Generate-letter CTA.
//
// Target flow (operator-locked):
//   1. CV       — select or generate ONE working CV
//   2. Letter   — primary action: generate cover letter
//   3. Fit      — run Analyse Fit ONCE on the selected CV
//   4. Review   — confirm + [Create Application Pack]
//
// (Step 5 in the operator's spec — "Application Pack" — is the
// resulting pack page navigated to after Review, not a wizard step.)
const STEPS = [
  { key: 'cv',     label: 'Choose CV',    shortLabel: 'CV'     },
  { key: 'letter', label: 'Cover letter', shortLabel: 'Letter' },
  { key: 'fit',    label: 'Analyse Fit',  shortLabel: 'Fit'    },
  { key: 'review', label: 'Review',       shortLabel: 'Review' },
];

// RC 1.48.6 — High-CV Value Guard threshold (mirrors the operator-
// locked backend constant `HIGH_CV_THRESHOLD` in
// `routes/cv_variants.py`). When the Source CV NDOR verdict for a
// job is ≥ this value, the wizard surfaces a pre-generate warning
// dialog so the user can confirm they want to spend Credits before
// a regeneration that may only deliver a presentation-level uplift.
const HIGH_CV_THRESHOLD = 80;

// Per-job, per-session sessionStorage key so the warning is shown
// at most once per (user session × job). Cleared when the browser
// tab closes (sessionStorage semantics).
const highCvSessionKey = (jid) => `ndor_high_cv_confirmed_${jid}`;

function StepIndicator({ stepIdx }) {
  return (
    <div
      className="flex items-center gap-1 sm:gap-2 text-[11px] sm:text-xs"
      data-testid="wizard-step-indicator"
    >
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          <span
            className={
              'px-1.5 sm:px-2 py-0.5 rounded transition-colors whitespace-nowrap ' +
              (i === stepIdx
                ? 'bg-ndor-gold/15 text-ndor-gold font-medium'
                : i < stepIdx
                  ? 'text-ndor-ink-muted'
                  : 'text-ndor-ink-muted/50')
            }
            data-testid={`wizard-step-${s.key}`}
          >
            {/* RC 1.41.F — short labels on mobile to fit four steps in
                one row without overflow; long labels keep the
                affordance on tablet+. */}
            <span className="sm:hidden">{i + 1}. {s.shortLabel}</span>
            <span className="hidden sm:inline">{i + 1}. {s.label}</span>
          </span>
          {i < STEPS.length - 1 && <span className="text-ndor-ink-muted/30">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Step 1 — Job + optional Analyse Fit ─────────────────────────
// RC 1.41.G4.G.1 — small inline plain-text excerpter for the JOB
// preview card. Some ingested jobs (e.g. older Stripe/Greenhouse/
// American Express rows) store their description as raw HTML or
// entity-escaped HTML. Without sanitising, React escapes those tags
// and the candidate sees a wall of `<div style="font-style: normal;
// margin: 0px;">` markup in the wizard's first card — a serious
// monetisation-trust regression flagged by the pre-monetisation
// regression sweep (iteration_26). JobDetailPage already uses the
// full `<JobDescription>` renderer, but the wizard card is a 3-line
// excerpt so we just want a tags-stripped, entity-decoded plain
// string. Pure utility, no new dep, no DOMPurify, no
// dangerouslySetInnerHTML.
function _toPlainExcerpt(s) {
  if (!s || typeof s !== 'string') return '';
  let t = s;
  // 1. Decode the most common entity-escaped HTML shapes
  //    ("&lt;p&gt;…&lt;/p&gt;") that real ingested rows use.
  if (t.includes('&lt;') || t.includes('&gt;') || t.includes('&amp;')) {
    try {
      const ta = document.createElement('textarea');
      ta.innerHTML = t;
      t = ta.value;
    } catch { /* SSR-safe fallback */ }
  }
  // 2. Strip every HTML tag (including stray <div style="…"> noise).
  t = t.replace(/<[^>]+>/g, ' ');
  // 3. Decode the residual named/numeric entities most JDs carry.
  t = t.replace(/&nbsp;/gi, ' ')
       .replace(/&amp;/gi,  '&')
       .replace(/&quot;/gi, '"')
       .replace(/&#39;|&apos;/gi, "'")
       .replace(/&lt;/gi,   '<')
       .replace(/&gt;/gi,   '>');
  // 4. Collapse runs of whitespace so `line-clamp-3` is honest.
  return t.replace(/\s+/g, ' ').trim();
}

function Step1Job({
  job, primaryCv, fitScore, fitDate, baselineStale,
  jobId, onContinue,
}) {
  const descExcerpt = _toPlainExcerpt(job?.description);
  // RC 1.42 P0.2 — Step 1 is DISPLAY-ONLY. Baseline Analyse Fit moved
  // to the Jobs page exclusively. The wizard never charges, never
  // calls /analyze-fit on the primary CV, and never blocks
  // progression. Missing baseline shows an advisory + secondary
  // link back to the Jobs page; user may still continue.
  // RC 1.46 — Source CV deterministic-match panel retired. Step 1
  // surfaces ONLY the LLM-graded NDOR Analyse-Fit baseline (paid
  // signal). Deterministic keyword overlap is internal-only.
  const hasBaseline = fitScore != null;
  const hasStale = !!baselineStale;
  return (
    <div className="space-y-4" data-testid="wizard-step-1-pane">
      <div className="rounded-lg border border-ndor-edge bg-ndor-surface-alt/30 p-4 space-y-1.5" data-testid="wizard-job-card">
        <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted">Job</p>
        <h3 className="text-base font-serif-display text-ndor-ink">{job?.title || 'Job'}</h3>
        <p className="text-xs text-ndor-ink-secondary">
          {job?.company || 'Company'}{job?.location ? ` · ${job.location}` : ''}
        </p>
        {descExcerpt && (
          <p
            className="text-xs text-ndor-ink-muted line-clamp-3 pt-2 leading-relaxed"
            data-testid="wizard-job-description-excerpt"
          >
            {descExcerpt}
          </p>
        )}
      </div>
      <div className="rounded-lg border border-ndor-edge p-4 space-y-3" data-testid="wizard-source-fit-row">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-ndor-ink-muted">Source CV</p>
            <p className="text-sm font-medium text-ndor-ink truncate">{primaryCv?.original_filename || 'Source CV'}</p>
          </div>
        </div>
        {/* Baseline NDOR Analyse-Fit row — display only */}
        <div className="border-t border-ndor-edge pt-3" data-testid="wizard-baseline-fit-row">
          <p className="text-xs text-ndor-ink-muted mb-1">
            Source CV · Baseline Fit
            <span className="ml-1 text-ndor-ink-muted/80">
              (NDOR analysis of your Source CV against this role)
            </span>
          </p>
          {hasBaseline ? (
            <div className="flex items-baseline justify-between gap-3" data-testid="wizard-baseline-present">
              <span className="text-xs text-ndor-ink-secondary">
                Score <span className="font-mono-ui tabular-nums text-ndor-ink">{fitScore}/100</span>
                <ScoreVariancePill size="xs" testIdSuffix="wizard-baseline" />
                {fitDate && <span className="text-ndor-ink-muted"> · {new Date(fitDate).toLocaleDateString()}</span>}
              </span>
              <Badge className="text-[10px] uppercase tracking-[0.14em] bg-ndor-success/15 text-ndor-success border-0">
                Already analysed
              </Badge>
            </div>
          ) : hasStale ? (
            <div
              className="text-xs text-ndor-ink-secondary space-y-1"
              data-testid="wizard-baseline-stale"
            >
              <p>
                Previous Baseline Fit: <span className="font-mono-ui tabular-nums">{baselineStale.fit_score}/100</span>
                &nbsp;against <span className="font-medium">{baselineStale.stale_cv_label}</span>.
                &nbsp;That score is no longer valid because your Source CV has changed.
              </p>
              <p className="text-ndor-ink-muted">
                Source CV Baseline Fit is only available from the Jobs page.
                You can continue without it, or
                {' '}
                <Link to={`/jobs/${jobId}`} className="text-ndor-gold underline" data-testid="wizard-back-to-job-link">
                  return to the Jobs page
                </Link>
                {' '}to refresh the baseline.
              </p>
            </div>
          ) : (
            <div
              className="text-xs text-ndor-ink-secondary space-y-1"
              data-testid="wizard-baseline-missing-advisory"
            >
              <p>
                No Baseline Fit yet for this Source CV.
              </p>
              <p className="text-ndor-ink-muted">
                Source CV Baseline Fit is only available from the Jobs page.
                You can continue without it, or
                {' '}
                <Link to={`/jobs/${jobId}`} className="text-ndor-gold underline" data-testid="wizard-back-to-job-link">
                  return to the Jobs page
                </Link>
                {' '}to run the baseline analysis.
              </p>
            </div>
          )}
        </div>
      </div>
      <AnalyseFitExplainer variant="expanded" testIdSuffix="wizard-step-1" />
      <div className="flex justify-end">
        <Button onClick={onContinue} data-testid="wizard-step-1-next">
          Next: Choose CV <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 2 — Choose CV ──────────────────────────────────────────
function Step2Cv({
  job, primaryCv, sourceFitNdor, sourceCvVerdict, hasBaselineFit, jobId,
  selectedCv, onSelectCv,
  generating, onGenerate, generatedResult, onPreviewVariant,
  progressMessage,
  creditsLoading,
  onBack, onContinue,
}) {
  const { usageCosts } = useCredits();
  const cvCost = getActionCost(usageCosts, 'cv_improve');
  const fitCost = getActionCost(usageCosts, 'analyze_fit');
  const cvLabel = cvCost == null ? '' : `${cvCost} ${cvCost === 1 ? 'Credit' : 'Credits'}`;
  const fitLabel = fitCost == null ? '' : `${fitCost} ${fitCost === 1 ? 'Credit' : 'Credits'}`;
  // RC 1.52 Phase 3A.4 — post-dispatch cooldown for the wizard's
  // Generate-CV button. Mirrors the workspace CvSection behaviour.
  const cvCooldown = useCooldown(cvVariantCooldownKey(primaryCv?.user_id, jobId));
  // RC 1.44.H4 — Recommended CVs retired. Single-CV-per-application
  // principle: every job application uses the Source CV OR a freshly
  // tailored CV generated for THIS job. No cross-job variant reuse,
  // no recommendation list, no keyword-overlap rankings to confuse
  // candidates with non-NDOR scores. Existing variants remain in
  // /documents as historical artifacts but are not surfaced here.
  // RC 1.46 — billing-protection scoring is now driven by the
  // LLM-graded NDOR CV verdict (`cv_sub_score`). Deterministic
  // keyword scores are demoted off this surface entirely. When no
  // Analyse Fit baseline exists for this Source CV + job pair, we
  // surface an explicit warning that tailoring will be charged
  // regardless of outcome — and we deep-link the user back to the
  // Jobs page to run Analyse Fit first.
  const canContinue = !!selectedCv;

  // Selection helpers so the same handler powers Source / Generated
  // cards consistently.
  const isSelected = (type, id) =>
    selectedCv?.type === type && (id == null || selectedCv?.id === id);

  // RC 1.46 — quality delta uses the LLM-graded `cv_sub_score`.
  // Prefer the value the finalize-charge response returned for the
  // GENERATED variant. For the source side, prefer the
  // finalize-charge response (matches the moment the comparison was
  // made), then fall back to the live baseline verdict
  // (`sourceCvVerdict`, hydrated from /baseline-fit) when the
  // backend didn't echo it back (e.g. CASE A, no_baseline_protection).
  const variantVerdict = generatedResult?.cv_sub_score_variant ?? null;
  const sourceVerdictForDelta =
    generatedResult?.cv_sub_score_source ?? sourceCvVerdict ?? null;
  const cvQualityDelta =
    variantVerdict != null && sourceVerdictForDelta != null
      ? variantVerdict - sourceVerdictForDelta
      : null;

  return (
    <div className="space-y-4" data-testid="wizard-step-2-pane">
      {/* ─── Generated now (lifted to top when present) ─── */}
      {generatedResult && (
        <div
          className={
            'rounded-lg border-2 p-3 space-y-2 ' +
            (isSelected('cv_variant', generatedResult.variant_id)
              ? 'border-ndor-gold bg-ndor-gold/5'
              : 'border-ndor-gold/40 bg-ndor-gold/[0.02]')
          }
          data-testid="wizard-cv-generated-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Sparkles className="h-4 w-4 text-ndor-gold shrink-0" />
                <p className="text-sm font-medium text-ndor-ink truncate">
                  {generatedResult.variant_name || 'Tailored Variant'}
                </p>
                <Badge className="text-[10px] uppercase tracking-[0.14em] bg-ndor-gold/15 text-ndor-gold border-0">
                  {generatedResult.reused ? 'Reused · saved earlier' : 'Tailored Variant · this attempt'}
                </Badge>
              </div>
              <p className="text-[11px] text-ndor-ink-muted leading-relaxed" data-testid="wizard-cv-generated-saved-line">
                {generatedResult.reused ? (
                  <>
                    We loaded your existing tailored CV for this job — no new Credits charged.
                    {' '}You can use it as-is or generate a fresh version below.
                  </>
                ) : (
                  <>Saved to <span className="font-mono-ui">Documents → CV Variants</span>.</>
                )}
              </p>
              <div className="flex items-center gap-3 text-[11px] text-ndor-ink-secondary flex-wrap pt-0.5">
                {generatedResult.cv_sub_score_variant != null ? (
                  <span data-testid="wizard-cv-generated-cv-verdict">
                    NDOR CV verdict <span className="font-mono-ui tabular-nums">{generatedResult.cv_sub_score_variant}/100</span>
                    {sourceVerdictForDelta != null && cvQualityDelta != null && (
                      <span className={
                        'ml-1 ' +
                        (cvQualityDelta > 0 ? 'text-ndor-success' : 'text-ndor-ink-muted')
                      }>
                        ({cvQualityDelta > 0 ? '+' : ''}{cvQualityDelta} vs Source {sourceVerdictForDelta}/100)
                      </span>
                    )}
                  </span>
                ) : (
                  <span
                    className="italic text-ndor-ink-muted"
                    data-testid="wizard-cv-generated-no-baseline-note"
                  >
                    NDOR CV verdict not graded — no baseline on this role.
                  </span>
                )}
                <span data-testid="wizard-cv-generated-fit-score">
                  Overall Fit{' '}
                  {generatedResult.fit_score_variant != null
                    ? <span className="font-mono-ui tabular-nums">{generatedResult.fit_score_variant}/100</span>
                    : <span className="italic text-ndor-ink-muted">not yet run</span>}
                  <span className="text-ndor-ink-muted ml-1">· informational</span>
                </span>
              </div>
              <p
                className={'text-[11px] ' + (generatedResult.charged ? 'text-ndor-warning' : 'text-ndor-ink-muted')}
                data-testid="wizard-cv-generated-charge-line"
              >
                {generatedResult.charged
                  ? (generatedResult.reason === 'cv_quality_improved'
                      ? `Material improvement achieved — ${cvLabel} charged (Δ +${generatedResult.delta ?? '?'} pts vs Source CV).`
                      : generatedResult.reason === 'no_baseline_protection'
                        ? `${cvLabel} charged — no Analyse Fit baseline existed, so improvement could not be measured.`
                        : `Tailoring complete — ${cvLabel} charged.`)
                  : generatedResult.reason === 'within_tolerance_band'
                    ? `Within NDOR confidence band — generated CV is comparable to your Source CV (Δ ${(generatedResult.delta ?? 0) >= 0 ? '+' : ''}${generatedResult.delta ?? 0} pts, ±${generatedResult.tolerance_band ?? 5}). No Credits charged.`
                    : generatedResult.reason === 'cv_quality_no_improvement'
                      ? `No material uplift detected — generated CV scored below your Source CV (Δ ${generatedResult.delta ?? '?'} pts). No Credits charged.`
                      : 'Tailoring not charged.'}
              </p>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <Button
                size="sm" variant="outline"
                onClick={() => onPreviewVariant(generatedResult.variant_id, generatedResult.variant_name)}
                data-testid="wizard-cv-generated-preview"
              >
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
              </Button>
              <Button
                size="sm"
                onClick={() => onSelectCv({
                  type: 'cv_variant',
                  id: generatedResult.variant_id,
                  score: generatedResult.cv_sub_score_variant ?? null,
                  name: generatedResult.variant_name,
                  label: 'Tailored Variant',
                })}
                data-testid="wizard-cv-generated-select"
                variant={isSelected('cv_variant', generatedResult.variant_id) ? 'default' : 'secondary'}
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                {isSelected('cv_variant', generatedResult.variant_id) ? 'Selected' : 'Use this CV'}
              </Button>
            </div>
          </div>

          {/* RC NDORFLOW.CLOSED-LOOP P1 — visible defence surfaces.
              Renders nothing when artifacts are absent (variant
              didn't beat source / pre-P1 cached variant) so the card
              keeps its current density for those flows. */}
          <OptimisationDefenseCards
            artifacts={generatedResult.optimisation_artifacts}
          />
        </div>
      )}

      {/* ─── Source CV row ─── */}
      <div
        className={
          'rounded-lg border p-3 transition-colors ' +
          (isSelected('base_cv') ? 'border-ndor-gold bg-ndor-gold/5' : 'border-ndor-edge')
        }
        data-testid="wizard-cv-option-source"
      >
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            onClick={() => onSelectCv({
              type: 'base_cv', id: primaryCv?.id, score: sourceFitNdor ?? null,
              name: primaryCv?.original_filename, label: 'Source CV',
            })}
            disabled={!primaryCv?.id}
            className="text-left min-w-0 flex-1"
            data-testid="wizard-cv-option-source-select"
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-ndor-ink-muted shrink-0" />
              <p className="text-sm font-medium text-ndor-ink truncate">Use your Source CV</p>
            </div>
            <p className="text-xs text-ndor-ink-muted truncate">{primaryCv?.original_filename || '—'}</p>
            <div className="flex items-center gap-3 text-[11px] mt-0.5 flex-wrap">
              <span className="text-ndor-success" data-testid="wizard-cv-source-no-charge">
                No charge to use your Source CV.
              </span>
              <span
                className="text-ndor-ink-secondary"
                data-testid="wizard-cv-source-cv-verdict"
              >
                NDOR CV verdict{' '}
                {sourceCvVerdict != null
                  ? <span className="font-mono-ui tabular-nums">{sourceCvVerdict}/100</span>
                  : <span className="italic text-ndor-ink-muted">not yet run</span>}
              </span>
              <span className="text-ndor-ink-secondary">
                Overall Fit{' '}
                {sourceFitNdor != null
                  ? <span className="font-mono-ui tabular-nums">{sourceFitNdor}/100</span>
                  : <span className="italic text-ndor-ink-muted">not yet run</span>}
              </span>
            </div>
          </button>
          {/* RC 1.49.x SOURCE_CV_NEVER_PREVIEWABLE — Source-CV
              preview affordance retired globally (operator-locked,
              2026-05-06). The wizard-cv-source-preview button has
              been removed. Tailored CV variant previews remain. */}
        </div>
      </div>

      {/* ─── Generate tailored CV ─── */}
      <div
        className={
          'rounded-lg border p-3 space-y-2 ' +
          (generatedResult ? 'border-ndor-edge/60 bg-ndor-surface-alt/20' : 'border-ndor-edge')
        }
        data-testid="wizard-cv-generate-block"
      >
        <p className="text-sm font-medium text-ndor-ink flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-ndor-gold" />
          {generatedResult
            ? (generatedResult.reused ? 'Generate a new version' : 'Generate another tailored CV')
            : 'Generate tailored CV'}
        </p>
        <p className="text-xs text-ndor-ink-secondary leading-relaxed">
          <strong>What may be charged:</strong>
        </p>
        <ul className="text-xs text-ndor-ink-secondary list-disc pl-5 space-y-0.5 leading-relaxed">
          <li>CV tailoring is charged {cvLabel || '—'} only when the generated CV improves on the LLM-graded NDOR CV verdict of your Source CV.</li>
          <li>If the generated CV does not improve, it is still saved for you to review, but no tailoring Credits are charged.</li>
          <li>Variant Fit on the selected tailored CV is a separate, optional step in Step 3 ({fitLabel || '—'}) — never auto-run after generation. Source CV Baseline Fit is run separately from the Jobs page.</li>
        </ul>
        {/* RC 1.46 — explicit no-baseline UX warning. Operator-mandated copy. */}
        {!hasBaselineFit && !generatedResult && (
          <div
            className="rounded-md border border-ndor-warning/40 bg-ndor-warning/5 px-3 py-2 text-[11px] text-ndor-ink-secondary flex items-start gap-2"
            data-testid="wizard-cv-no-baseline-warning"
          >
            <AlertCircle className="h-4 w-4 text-ndor-warning mt-0.5 shrink-0" />
            <span>
              No Source CV Baseline Fit on this role yet. Tailoring will be charged regardless of outcome.{' '}
              <Link
                to={`/jobs/${jobId}`}
                className="text-ndor-gold underline font-medium"
                data-testid="wizard-cv-no-baseline-warning-link"
              >
                Run Baseline Fit first to unlock billing protection
              </Link>
              .
            </span>
          </div>
        )}
        <Button
          onClick={onGenerate}
          disabled={generating || creditsLoading || cvCooldown.active}
          variant={generatedResult ? 'outline' : 'default'}
          data-testid="wizard-cv-generate-button"
          data-cooldown-active={cvCooldown.active ? '1' : '0'}
          data-cooldown-remaining-ms={cvCooldown.active ? String(cvCooldown.remainingMs) : ''}
          title={cvCooldown.active
            ? `Generate is on cooldown for ${cvCooldown.label}. This prevents accidental re-clicks while your wallet syncs.`
            : undefined}
        >
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
          {cvCooldown.active
            ? `Generate again in ${cvCooldown.label}`
            : `Generate${cvCost != null ? ` · up to ${cvCost} ${cvCost === 1 ? 'Credit' : 'Credits'}` : ''}`}
        </Button>
        {/* RC 1.49.5A P0-A3 (operator-locked, 2026-05-08) — calm
            "Loading your wallet…" hint while credits hydration is
            in flight. The button above is disabled in the same
            condition, so no chargeable backend request can fire
            during this window. */}
        {creditsLoading && !generating && (
          <div
            role="status"
            aria-live="polite"
            data-testid="wizard-cv-credits-loading-hint"
            className="mt-2 flex items-center gap-2 text-xs text-ndor-ink-muted"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            <span>Loading your wallet…</span>
          </div>
        )}
        {/* RC 1.49.x Slice 4 — calm rotating reassurance copy. */}
        {progressMessage && (
          <div
            role="status"
            aria-live="polite"
            data-testid="wizard-cv-progress"
            className="mt-2 flex items-center gap-2 text-xs text-ndor-ink-secondary"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 text-ndor-ink-muted" />
            <span data-testid="wizard-cv-progress-message">{progressMessage}</span>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} data-testid="wizard-step-2-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        <Button onClick={onContinue} disabled={!canContinue} data-testid="wizard-step-2-next">
          Next: Cover Letter <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3 — Selected-CV Analyse-Fit (optional) + Cover Letter ───
//
// RC 1.41.G4.D — Step 3 now hosts TWO independent optional actions:
//   1. Analyse Fit on the SELECTED CV (Source / Recommended /
//      Generated alike). Uses the existing `/analyze-fit` endpoint
//      with `source_type` derived from the selection. Best-fit
//      carry-forward: if the new fit beats the existing best, it
//      becomes the new best; otherwise the previous best is kept.
//   2. Cover-letter generation (existing).
//
// Either, both, or neither can be skipped. Pack creation is never
// blocked by either action.

function Step3SelectedCvFit({
  jobId, selectedCv, onAnalyse, analysing, bestFit, lastResult, baselineFit, fitSnapshot,
}) {
  const target = selectedCv?.label || 'selected CV';
  const { usageCosts } = useCredits();
  const fitCost = getActionCost(usageCosts, 'analyze_fit');
  const fitLabel = fitCost == null ? '' : ` · ${fitCost} ${fitCost === 1 ? 'Credit' : 'Credits'}`;

  return (
    <div
      className="rounded-lg border border-ndor-edge p-3 space-y-2"
      data-testid="wizard-step-3-selected-fit"
    >
      <p className="text-sm font-medium text-ndor-ink flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-ndor-gold" />
        Variant Fit on selected tailored CV (optional)
      </p>
      <p className="text-xs text-ndor-ink-secondary leading-relaxed">
        Runs NDOR's strategic analysis against your <span className="font-medium">{target}</span> — a tailored Variant Fit, separate from the Source CV Baseline Fit. Includes CV review.{' '}
        <span className="text-ndor-ink-muted">
          Required for full interview-preparation intelligence in your application pack.
        </span>
      </p>

      <AnalyseFitExplainer variant="compact" testIdSuffix="wizard-step-3" />

      {/* RC 1.47.5 Phase 3 — full Source / V<N> ladder so users see
          every analysis on file for this job. Closes the silent
          V1-hiding bug. Suppressed when the canonical-fit feature
          flag is OFF. */}
      <Step3ScoreLandscape
        jobId={jobId}
        selectedCv={selectedCv}
        activeRunVariantId={lastResult?.variantId || null}
        fitSnapshot={fitSnapshot}
      />

      {/* Current best summary (carry-forward source-of-truth) */}
      {bestFit?.score != null && (
        <p
          className="text-[11px] text-ndor-ink-secondary"
          data-testid="wizard-step-3-best-fit-summary"
        >
          Current best Analyse-Fit:{' '}
          <span className="font-mono-ui tabular-nums text-ndor-ink">
            {bestFit.score}/100
          </span>
          <ScoreVariancePill size="xs" testIdSuffix="wizard-current-best" />
          {bestFit.label && <> · from <span className="italic">{bestFit.label}</span></>}
        </p>
      )}

      {/* Last-result diff line — only shown when the user has just run
          an analyse-fit at this step (not on initial load).
          RC 1.47.CANONICAL-LATEST-RUN-GOVERNANCE — copy rewritten
          to drop "strongest" / "previous best" / "still the
          strongest — keeping that for your pack" promotion
          language. Latest always replaces; we tell the user the
          delta vs the previous-latest, never historical-best. */}
      {lastResult && (
        <p
          className="text-[11px] text-ndor-ink-muted"
          data-testid="wizard-step-3-last-fit-line"
        >
          {lastResult.previousLatest != null
            ? `New Analyse-Fit ${lastResult.newScore}/100 — now your latest for this job (was ${lastResult.previousLatest}/100). Used in your pack.`
            : `New Analyse-Fit ${lastResult.newScore}/100 recorded. Used in your pack.`}
        </p>
      )}

      {/* RC 1.42 P0.5 — "What changed vs baseline" reasoning delta.
          Renders only when:
            • Step 3 just produced a `lastResult` AND
            • the run carried `analysis` sub-scores AND
            • the user has a baseline row with sub-scores to diff against
          The diff is purely client-side — no extra LLM call, no extra
          backend round-trip. The numbers come from the same JSON the
          baseline-fit endpoint already returned + the analyse-fit
          response that just came back. */}
      {(() => {
        const a = lastResult?.analysis;
        const b = baselineFit?.baseline?.sub_scores;
        if (!a || !b) return null;
        if (lastResult.newScore == null || baselineFit.baseline.fit_score == null) return null;
        const dims = [
          { key: 'salary_score',    label: 'Salary'    },
          { key: 'seniority_score', label: 'Seniority' },
          { key: 'location_score',  label: 'Location'  },
          { key: 'strategic_score', label: 'Strategic' },
          { key: 'risk_score',      label: 'Risk'      },
        ];
        const diffs = dims
          .map(d => {
            const after  = a[d.key];
            const before = b[d.key];
            if (typeof after !== 'number' || typeof before !== 'number') return null;
            const delta = Math.round((after <= 1 ? after * 100 : after))
                        - Math.round((before <= 1 ? before * 100 : before));
            return { ...d, delta };
          })
          .filter(Boolean);
        const overallDelta = lastResult.newScore - baselineFit.baseline.fit_score;
        const biggest = diffs.length > 0
          ? diffs.reduce((m, x) => Math.abs(x.delta) > Math.abs(m.delta) ? x : m)
          : null;
        const gainers = diffs.filter(d => d.delta > 0).sort((a_, b_) => b_.delta - a_.delta);
        const losers  = diffs.filter(d => d.delta < 0).sort((a_, b_) => a_.delta - b_.delta);
        return (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2.5 space-y-1.5"
            data-testid="wizard-step-3-reasoning-delta"
          >
            <p className="text-[10px] font-mono-ui uppercase tracking-[0.18em] text-ndor-gold flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> What changed vs baseline
            </p>
            <p className="text-xs text-ndor-ink-secondary leading-relaxed">
              Overall NDOR verdict moved from{' '}
              <span className="font-mono-ui tabular-nums">{baselineFit.baseline.fit_score}</span>
              {' → '}
              <span className="font-mono-ui tabular-nums text-ndor-ink">{lastResult.newScore}</span>
              {' '}
              <span className={overallDelta > 0 ? 'text-ndor-success' : (overallDelta < 0 ? 'text-ndor-warning' : 'text-ndor-ink-muted')}>
                ({overallDelta > 0 ? '+' : ''}{overallDelta})
              </span>.
            </p>
            {biggest && (
              <p className="text-xs text-ndor-ink-secondary">
                • Biggest move:{' '}
                <span className="font-medium">{biggest.label}</span>{' '}
                <span className={biggest.delta > 0 ? 'text-ndor-success' : 'text-ndor-warning'}>
                  ({biggest.delta > 0 ? '+' : ''}{biggest.delta})
                </span>.
              </p>
            )}
            {gainers.length > 0 && (
              <p className="text-xs text-ndor-ink-secondary">
                • Improved:{' '}
                {gainers.map((g, i) => (
                  <span key={g.key}>
                    {i > 0 ? ', ' : ''}
                    <span className="font-medium">{g.label}</span>
                    <span className="text-ndor-success"> +{g.delta}</span>
                  </span>
                ))}.
              </p>
            )}
            {losers.length > 0 && (
              <p className="text-xs text-ndor-ink-muted">
                • Lost ground:{' '}
                {losers.map((l, i) => (
                  <span key={l.key}>
                    {i > 0 ? ', ' : ''}
                    <span className="font-medium">{l.label}</span>
                    <span className="text-ndor-warning"> {l.delta}</span>
                  </span>
                ))}.
              </p>
            )}
          </div>
        );
      })()}

      <Button
        onClick={() => onAnalyse()}
        disabled={analysing || !selectedCv?.id}
        variant="outline"
        size="sm"
        data-testid="wizard-step-3-analyse-fit-button"
      >
        {analysing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
        {bestFit?.score != null && bestFit?.sourceId === selectedCv?.id
          ? `Re-run Analyse Fit${fitLabel}`
          : `Analyse Fit on selected CV${fitLabel}`}
      </Button>
    </div>
  );
}

// ─── Step 3 — Analyse Fit (RC 1.49.2 dedicated step) ──────────────
//
// Single-result-only Analyse Fit step. Promoted out of the cover
// letter step (which used to render `Step3SelectedCvFit`) so the
// flow is strictly linear: CV → Letter → Fit → Review.
//
// Hard rails:
//   • Shows ONLY the latest result for the selected CV (no `all_cached`
//     ladder, no "alternatives" panel, no "compare runs" UI).
//   • Does NOT include the cover letter in the score (apples-to-apples
//     with Job Detail's Source-CV baseline).
//   • Read-only `bestFit` snapshot is used for "current result on
//     file" hydration — no extra fetch beyond the canonical
//     `/api/jobs/{id}/best-fit` already loaded by the wizard
//     bootstrap.
function Step3AnalyseFit({
  jobId, selectedCv, onAnalyse, analysing, bestFit, lastResult,
  baselineFit,
  creditsLoading,
  userId,
  onBack, onContinue,
}) {
  const { usageCosts } = useCredits();
  const fitCost  = getActionCost(usageCosts, 'analyze_fit');
  const fitLabel = fitCost == null ? '' : ` · ${fitCost} ${fitCost === 1 ? 'Credit' : 'Credits'}`;
  const target   = selectedCv?.label || 'selected CV';
  // RC 1.52 Phase 3B.1 — wizard Step-3 Analyse-Fit cooldown,
  // sourceId-scoped so swapping the selected CV unlocks the
  // button for the new (user, job, source) tuple.
  const stepCooldown = useCooldown(analyseFitCooldownKey(
    userId, jobId, selectedCv?.id || 'no_source',
  ));

  // RC 1.49.x.RUN_ISOLATION (operator-locked, 2026-05-06) — Step 3
  // shows results ONLY for fits run IN THIS WIZARD RUN. The
  // wizard-bootstrap baseline-fit hydration is intentionally NOT
  // wired into Step 3's "Current result" panel. Step 3 reads
  // `lastResult` only.
  const hasResult = !!lastResult;
  // RC 1.49.2 P0-FIN-LIFECYCLE-2 (2026-05-08) — Next-button gating.
  // The result must MATCH the currently selected CV/variant for the
  // Next button to enable. Without this match check a stale result
  // (from a previously-selected source) would let the user advance
  // into Review with the wrong score bound to the pack.
  const lastResultMatchesSelection = !!(
    hasResult
    && lastResult?.sourceType
    && lastResult?.sourceId
    && selectedCv?.id
    && (
      (lastResult.sourceType === 'cv_variant'
        && selectedCv.type === 'cv_variant'
        && lastResult.sourceId === selectedCv.id)
      || (lastResult.sourceType === 'uploaded_cv'
        && (selectedCv.type === 'base_cv' || selectedCv.type === 'uploaded_cv')
        && lastResult.sourceId === selectedCv.id)
    )
  );
  const canContinueAfterFit = !analysing && lastResultMatchesSelection;
  const score   = lastResult?.newScore ?? null;
  const cvLabel = lastResult?.cvLabel ?? target;
  const reasoning = lastResult?.analysis || lastResult?.reasoning || null;

  // RC 1.49.x.RUN_ISOLATION — operator activation rule: when the
  // user selected the Base/Source CV, Analyse Fit is NEITHER
  // visible NOR runnable from this step. The Base CV already has
  // a baseline score on the Job page; surfacing Analyse Fit here
  // would be redundant and would invite cross-run reuse. Tailored
  // CV path keeps Analyse Fit available (manually clicked, never
  // auto-run).
  const isBaseCvSelected = selectedCv?.type === 'base_cv';

  // RC 1.49.3 P1-8 — light delta badge against the Source-CV
  // baseline. Renders only when (a) we have a current fit score
  // for the selected CV, (b) a Source-CV baseline is on file, and
  // (c) the selected CV is NOT the Source CV itself (which would
  // make the delta trivially zero).
  const baselineScore = baselineFit?.baseline?.fit_score ?? null;
  const showDelta = (
    score != null
    && baselineScore != null
    && selectedCv?.type === 'cv_variant'
  );
  const delta = showDelta ? Math.round(score - baselineScore) : null;

  if (isBaseCvSelected) {
    return (
      <div className="space-y-4" data-testid="wizard-step-fit-pane">
        <div
          className="rounded-lg border border-ndor-edge p-4 space-y-2"
          data-testid="wizard-step-fit-base-cv-notice"
        >
          <p className="text-sm font-medium text-ndor-ink flex items-center gap-2">
            <FileText className="h-4 w-4 text-ndor-ink-muted" /> Analyse Fit not required
          </p>
          <p className="text-xs text-ndor-ink-secondary leading-relaxed">
            You selected your Source CV. Its baseline score for this job is already available on the Job page; running Analyse Fit again here would be redundant. Continue to Review to create your pack.
          </p>
        </div>
        <div className="flex justify-between items-center">
          <Button variant="ghost" onClick={onBack} data-testid="wizard-step-fit-back">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </Button>
          <Button onClick={onContinue} data-testid="wizard-step-fit-next">
            Next: Review <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="wizard-step-fit-pane">
      <div className="rounded-lg border border-ndor-edge p-4 space-y-3">
        <p className="text-sm font-medium text-ndor-ink flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-ndor-gold" /> Analyse Fit on selected CV
        </p>
        <p className="text-xs text-ndor-ink-secondary leading-relaxed">
          NDOR&apos;s strategic analysis runs against your{' '}
          <span className="font-medium">{target}</span> and the job description.
          The cover letter is intentionally excluded so this score stays
          comparable to your Source-CV baseline. We display the latest
          result only — no historical runs.
        </p>

        <Button
          onClick={() => onAnalyse()}
          disabled={analysing || !selectedCv?.id || creditsLoading || stepCooldown.active}
          variant="outline"
          size="sm"
          data-testid="wizard-step-fit-analyse-button"
          data-cooldown-active={stepCooldown.active ? '1' : '0'}
          data-cooldown-remaining-ms={stepCooldown.active ? String(stepCooldown.remainingMs) : ''}
          title={stepCooldown.active
            ? `Analyse Fit is on cooldown for ${stepCooldown.label}. This prevents accidental re-clicks while your wallet syncs.`
            : undefined}
        >
          {analysing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
          {stepCooldown.active
            ? `Run again in ${stepCooldown.label}`
            : (hasResult ? `Re-run Analyse Fit${fitLabel}` : `Analyse Fit${fitLabel}`)}
        </Button>
        {/* RC 1.49.5A P0-A3 — credits-loading gate. */}
        {creditsLoading && !analysing && (
          <div
            role="status"
            aria-live="polite"
            data-testid="wizard-fit-credits-loading-hint"
            className="mt-2 flex items-center gap-2 text-xs text-ndor-ink-muted"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            <span>Loading your wallet…</span>
          </div>
        )}

        {hasResult && (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 p-3 space-y-2"
            data-testid="wizard-step-fit-current-result"
          >
            <div className="flex items-baseline justify-between">
              <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-gold font-mono-ui">
                Current result
              </p>
              <p className="text-[11px] text-ndor-ink-muted truncate max-w-[55%]" title={cvLabel}>
                {cvLabel}
              </p>
            </div>
            <div className="flex items-baseline gap-2">
              <span
                className="font-serif text-3xl leading-none text-ndor-ink"
                data-testid="wizard-step-fit-score"
              >
                {score}
              </span>
              <span className="text-sm text-ndor-ink-muted">/ 100 fit</span>
              {showDelta && (
                <span
                  className={
                    'ml-1 text-[11px] font-mono-ui tabular-nums px-1.5 py-0.5 rounded ' +
                    (delta > 0
                      ? 'bg-ndor-success/15 text-ndor-success'
                      : delta < 0
                        ? 'bg-ndor-warning/15 text-ndor-warning'
                        : 'bg-ndor-surface-alt text-ndor-ink-muted')
                  }
                  data-testid="wizard-step-fit-delta-badge"
                  title={`Compared to your Source CV baseline of ${baselineScore}/100`}
                >
                  {delta > 0 ? '+' : ''}{delta} vs Source CV
                </span>
              )}
            </div>
            {reasoning?.score_explanation && (
              <p
                className="text-[12px] text-ndor-ink-secondary leading-relaxed"
                data-testid="wizard-step-fit-explanation"
              >
                {reasoning.score_explanation}
              </p>
            )}
            {Array.isArray(reasoning?.key_strengths) && reasoning.key_strengths.length > 0 && (
              <div data-testid="wizard-step-fit-strengths">
                <p className="text-[11px] font-medium text-ndor-ink mt-1">Key strengths</p>
                <ul className="list-disc pl-5 text-[12px] text-ndor-ink-secondary leading-relaxed">
                  {reasoning.key_strengths.slice(0, 3).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(reasoning?.key_risks) && reasoning.key_risks.length > 0 && (
              <div data-testid="wizard-step-fit-risks">
                <p className="text-[11px] font-medium text-ndor-ink mt-1">Key risks</p>
                <ul className="list-disc pl-5 text-[12px] text-ndor-ink-secondary leading-relaxed">
                  {reasoning.key_risks.slice(0, 3).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {!hasResult && (
          <p
            className="text-[12px] text-ndor-ink-muted italic"
            data-testid="wizard-step-fit-empty"
          >
            No fit analysis on file for this CV yet. Run one above to see your score, key strengths, and risks before continuing.
          </p>
        )}
      </div>

      <div className="flex justify-between items-center">
        <Button variant="ghost" onClick={onBack} data-testid="wizard-step-fit-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        <Button
          onClick={onContinue}
          disabled={!canContinueAfterFit}
          data-testid="wizard-step-fit-next"
        >
          Next: Review <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 2 — Cover Letter (RC 1.49.2 — fit panel removed) ────────
function Step3Letter({
  letterDraft, setLetterDraft, generating, onGenerate, onSkip, onBack, onContinue, hasLetter,
  progressMessage,
  creditsLoading,
}) {
  const { usageCosts } = useCredits();
  const letterCost = getActionCost(usageCosts, 'cover_letter');
  const letterLabel = letterCost == null ? '' : `${letterCost} ${letterCost === 1 ? 'Credit' : 'Credits'}`;
  return (
    <div className="space-y-4" data-testid="wizard-step-3-pane">
      <div className="rounded-lg border border-ndor-edge p-3 space-y-2">
        <p className="text-sm font-medium text-ndor-ink flex items-center gap-2">
          <Mail className="h-4 w-4 text-ndor-gold" /> Cover letter
        </p>
        {/* RC 1.49.x.RUN_ISOLATION (2026-05-06) — historical reuse
            banner removed. The wizard never hydrates a previous
            cover letter on mount; users always start with a fresh
            slate or click Generate explicitly in this run. */}
        <p className="text-xs text-ndor-ink-secondary leading-relaxed">
          {letterCost == null
            ? 'Generate a cover letter for this job. You can edit and save before continuing.'
            : `Generate a cover letter for this job. Cost: ${letterLabel}. You can edit and save before continuing.`}
        </p>
        <Button
          onClick={onGenerate}
          disabled={generating || creditsLoading}
          variant="outline"
          data-testid="wizard-letter-generate-button"
        >
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
          {hasLetter
            ? `Regenerate${letterCost != null ? ` · ${letterLabel}` : ''}`
            : `Generate${letterCost != null ? ` · ${letterLabel}` : ''}`}
        </Button>
        {/* RC 1.49.5A P0-A3 — credits-loading gate. */}
        {creditsLoading && !generating && (
          <div
            role="status"
            aria-live="polite"
            data-testid="wizard-letter-credits-loading-hint"
            className="mt-2 flex items-center gap-2 text-xs text-ndor-ink-muted"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            <span>Loading your wallet…</span>
          </div>
        )}
        {/* RC 1.49.x Slice 4 — calm rotating reassurance copy. */}
        {progressMessage && (
          <div
            role="status"
            aria-live="polite"
            data-testid="wizard-letter-progress"
            className="mt-2 flex items-center gap-2 text-xs text-ndor-ink-secondary"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 text-ndor-ink-muted" />
            <span data-testid="wizard-letter-progress-message">{progressMessage}</span>
          </div>
        )}
      </div>
      {hasLetter && (
        <div className="space-y-1.5" data-testid="wizard-letter-editor">
          <p className="text-xs text-ndor-ink-muted">Edit before saving (locks at Review).</p>
          <Textarea
            value={letterDraft}
            onChange={(e) => setLetterDraft(e.target.value)}
            rows={10}
            className="font-manrope text-sm"
            data-testid="wizard-letter-textarea"
          />
        </div>
      )}
      <div className="flex justify-between items-center">
        <Button variant="ghost" onClick={onBack} data-testid="wizard-step-3-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onSkip} className="text-xs text-ndor-ink-muted hover:text-ndor-ink underline" data-testid="wizard-letter-skip">
            Skip cover letter
          </button>
          <Button
            onClick={onContinue}
            disabled={generating}
            data-testid="wizard-step-3-next"
          >
            Next: Analyse Fit <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 4 — Review ─────────────────────────────────────────────
function Step4Review({
  job, primaryCv, fitScore, fitDate, selectedCv,
  generatedResult, letterId, letterDraft, letterTitle, packName,
  setPackName, notes, setNotes, creating, onBack, onCreate,
  progressMessage,
  creditsLoading,
  onPreviewCv, onPreviewLetter, bestFit, onBackToFitStep,
}) {
  const { usageCosts } = useCredits();
  const fitCost = getActionCost(usageCosts, 'analyze_fit');
  const fitLabel = fitCost == null ? '' : ` · ${fitCost} ${fitCost === 1 ? 'Credit' : 'Credits'}`;
  // RC 1.46 — Selected CV summary line surfaces the LLM-graded
  // signals only. Deterministic CV-score is no longer rendered here.
  const selectedCvVerdict =
    selectedCv?.type === 'cv_variant' && generatedResult?.cv_sub_score_variant != null
      ? generatedResult.cv_sub_score_variant
      : null;
  const selectedCvFit =
    selectedCv?.type === 'base_cv' && fitScore != null
      ? fitScore
      : selectedCv?.type === 'cv_variant' && generatedResult?.fit_score_variant != null
        ? generatedResult.fit_score_variant
        : null;
  return (
    <div className="space-y-4" data-testid="wizard-step-4-pane">
      <div className="space-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted mb-1">Pack name</p>
          {/* RC 1.52 Phase 3A.DUP — Pack names are SYSTEM-generated and
              collision-proof. Users can delete or export; renaming is
              done locally after download. */}
          <Input
            value={packName}
            readOnly
            data-testid="wizard-review-pack-name"
          />
          <p className="text-[11px] text-ndor-ink-muted mt-1" data-testid="wizard-review-pack-name-hint">
            System-generated. You can rename your downloaded file locally.
          </p>
        </div>
        <div className="rounded-lg border border-ndor-edge p-3 space-y-1" data-testid="wizard-review-job">
          <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted">Job</p>
          <p className="text-sm text-ndor-ink">{job?.title} · {job?.company}</p>
        </div>
        <div className="rounded-lg border border-ndor-edge p-3 space-y-1" data-testid="wizard-review-cv">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted">Selected CV</p>
              <p className="text-sm text-ndor-ink truncate">{selectedCv?.label} — {selectedCv?.name}</p>
              <p className="text-xs text-ndor-ink-muted">
                {selectedCvVerdict != null && (
                  <>NDOR CV verdict {selectedCvVerdict}/100</>
                )}
                {selectedCvVerdict != null && selectedCvFit != null && ' · '}
                {selectedCvFit != null && (
                  <>Overall Fit {selectedCvFit}/100{fitDate && selectedCv?.type === 'base_cv' ? ` · ${new Date(fitDate).toLocaleDateString()}` : ''}</>
                )}
                {selectedCvVerdict == null && selectedCvFit == null && '—'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={onPreviewCv} data-testid="wizard-review-preview-cv">
              <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
            </Button>
          </div>
        </div>

        {/* RC 1.41.G4.E — Best Analyse-Fit + interview-prep transparency.
            When a fit exists for this (user, job), surface it here and
            tell the user the pack will compose with it (which unlocks
            interview prep on the pack-detail page). When no fit
            exists, show a non-blocking nudge with a Back-to-Step-3
            shortcut. Pack creation proceeds either way. */}
        <div
          className={
            'rounded-lg border p-3 space-y-1 ' +
            (bestFit?.score != null
              ? 'border-ndor-edge bg-ndor-surface-alt/30'
              : 'border-ndor-gold/40 bg-ndor-gold/[0.03]')
          }
          data-testid="wizard-review-fit"
        >
          <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted">
            Application-readiness (Analyse-Fit)
          </p>
          {bestFit?.score != null ? (
            <>
              <p className="text-sm text-ndor-ink" data-testid="wizard-review-fit-score">
                {/* RC 1.47.CANONICAL-LATEST-RUN-GOVERNANCE — "Best
                    fit:" → "Latest fit:". Same value (now bound
                    via latest-wins query) but truthful copy. */}
                Latest fit:{' '}
                <span className="font-mono-ui tabular-nums">{bestFit.score}/100</span>
                {bestFit.label && (
                  <span className="text-ndor-ink-muted text-xs">
                    {' · '}from <span className="italic">{bestFit.label}</span>
                  </span>
                )}
              </p>
              <p className="text-[11px] text-ndor-ink-secondary leading-relaxed">
                Used in your pack — unlocks NDOR's interview preparation panel
                (gap-aware questions, repositioning advice, risk briefings) on the pack page.
              </p>
            </>
          ) : (
            <>
              {/* RC 1.49.5A P0-A2 (operator-locked, 2026-05-08) —
                  explicit Interview Pack unavailability copy when no
                  Analyse Fit row is bound to this workflow. The
                  user MAY still create the Application Pack
                  (no behaviour change — the Review Next button
                  remains enabled here). The Interview Pack alone
                  is the gated artefact. */}
              <p
                className="text-sm text-ndor-ink flex items-center gap-1.5"
                data-testid="wizard-review-fit-locked"
              >
                <Sparkles className="h-3.5 w-3.5 text-ndor-gold" />
                Interview Pack unavailable until Analyse Fit is completed for this application workflow.
              </p>
              <p className="text-[11px] text-ndor-ink-secondary leading-relaxed">
                Optional — your pack will still be created. With a valid Variant Fit on the selected tailored CV, the pack
                page surfaces gap-aware interview questions, repositioning advice, and risk briefings
                tailored to this role. Source-CV-only packs ship the Baseline Fit instead.
              </p>
              <button
                type="button"
                onClick={onBackToFitStep}
                className="text-[11px] underline text-ndor-ink-muted hover:text-ndor-ink"
                data-testid="wizard-review-back-to-fit"
              >
                Back to Step 3 · Run Variant Fit{fitLabel}
              </button>
            </>
          )}
        </div>
        <div className="rounded-lg border border-ndor-edge p-3 space-y-1" data-testid="wizard-review-letter">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted">Cover letter</p>
              <p className="text-sm text-ndor-ink truncate">
                {letterId ? (letterTitle || 'Generated cover letter') : 'Skipped'}
              </p>
              {letterId && (
                <p className="text-xs text-ndor-ink-muted flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Locked at review
                </p>
              )}
            </div>
            {letterId && (
              <Button variant="outline" size="sm" onClick={onPreviewLetter} data-testid="wizard-review-preview-letter">
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
              </Button>
            )}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted mb-1">Notes</p>
          <Textarea
            value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="e.g. follow up in 2 weeks"
            data-testid="wizard-review-notes"
          />
        </div>
      </div>
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} data-testid="wizard-step-4-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        <Button onClick={onCreate} disabled={creating || !packName.trim() || creditsLoading} data-testid="wizard-step-4-create">
          {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
          Create application pack · Free
        </Button>
      </div>
      {/* RC 1.49.5A P0-A3 — credits-loading gate. Pack creation is
          free today but is gated alongside paid actions for
          consistency: while wallet/usage data is hydrating, no
          generation flow can fire. */}
      {creditsLoading && !creating && (
        <div
          role="status"
          aria-live="polite"
          data-testid="wizard-pack-credits-loading-hint"
          className="flex items-center gap-2 text-xs text-ndor-ink-muted"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
          <span>Loading your wallet…</span>
        </div>
      )}
      {/* RC 1.49.x Slice 4 — calm rotating reassurance copy. */}
      {progressMessage && (
        <div
          role="status"
          aria-live="polite"
          data-testid="wizard-pack-progress"
          className="flex items-center gap-2 text-xs text-ndor-ink-secondary"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 text-ndor-ink-muted" />
          <span data-testid="wizard-pack-progress-message">{progressMessage}</span>
        </div>
      )}
      {/* RC 1.41.G4.F — Pack creation is now free. Monetisation lives
          on Analyse Fit (live cost surfaced via `usageCosts.analyze_fit`)
          which unlocks the Interview Intelligence panel on the pack
          detail page. The value block below explains what the free
          pack archives. */}
      <div
        className="rounded-lg border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2.5 sm:px-4 sm:py-3 text-xs text-ndor-ink-secondary leading-relaxed"
        data-testid="wizard-review-value-block"
      >
        <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui mb-1.5">
          What's in your pack
        </p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>An immutable application pack pinned to this job. The pack contains <strong>only the artefacts you generated</strong> — your tailored CV and/or cover letter — plus whichever fit analysis is bound (Source CV Baseline Fit, or a tailored Variant Fit).</li>
          <li>Source-CV-only path: pack carries the Source CV reference + Baseline Fit. No variant, no cover letter — and that's fine.</li>
          <li>Tracking on the Applications page (status, notes, follow-ups).</li>
          <li>One-click export of whatever the pack contains, when you're ready to apply.</li>
          <li>Any tailored CV and any cover letter stay in your Documents library for re-use on similar roles.</li>
          <li>If a valid Variant Fit exists on the pack's tailored CV, NDOR's Interview Intelligence panel unlocks on the pack page. Source-CV-only packs export the baseline analysis without it.</li>
          <li>You remain in control of submission. NDOR never auto-applies on your behalf.</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Read-only preview modal ─────────────────────────────────────
// RC 1.41.G — wizard preview modal now uses the shared CvRenderer.
//   previewOpen shape:
//     { title, mode: 'source'|'variant'|'letter', data: <row> }
function PreviewModal({ open, onClose, title, mode, data, loading }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="wizard-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            <Badge variant="outline" className="text-[10px] tracking-[0.16em] uppercase border-ndor-edge text-ndor-ink-muted">
              <Lock className="h-2.5 w-2.5 mr-1" />
              Read-only
            </Badge>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Read-only preview of the selected document. No edits are possible from this dialog.
          </DialogDescription>
        </DialogHeader>
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted mx-auto my-6" /> : (
          <CvRenderer mode={mode || 'source'} data={data} showDisclaimer />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────
export default function JobApplicationWizard({ open, onOpenChange, job, onPackCreated }) {
  const navigate = useNavigate();
  const jobId = job?.id;

  // RC 1.49.x.PACK-FINALITY — pack-page deep-link hydration removed.
  // Packs are immutable historical records; the pack page no longer
  // emits `?pack_id=…&step=fit&cv_variant_id=…` deep links and the
  // wizard therefore has no `pack_id` / `step` / `cv_variant_id` URL
  // params to consume. To analyse a different CV, the user starts a
  // NEW application flow from the Jobs page.

  const [stepIdx, setStepIdx] = useState(0);
  const [primaryCv, setPrimaryCv] = useState(null);
  // RC 1.46 — `sourceFit` state (deterministic Source-CV keyword
  // score) retired alongside the Step 1 / Step 2 deterministic
  // surfaces. The wizard now reads only the LLM-graded baseline
  // (`fitScore` from `/baseline-fit`) for trust signals.
  // RC 1.44.H4 — `variants` state retired alongside the recommended-CV
  // surface. Generated variants persist via `routes/cv_variants.py` but
  // are only surfaced on /documents (historical artifact); the wizard
  // never lists them again. Single-CV-per-application principle.
  const [fitScore, setFitScore] = useState(null);
  const [fitDate, setFitDate] = useState(null);
  // RC 1.42 P0.2 — `analysing` state was tied to the now-removed
  // Step 1 paid Analyse Fit. Deleted because nothing else consumes
  // it (Step 3 has its own `step3FitRunning`).

  const [selectedCv, setSelectedCv] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);

  const [letterDraft, setLetterDraft] = useState('');
  const [letterId, setLetterId] = useState(null);
  const [letterTitle, setLetterTitle] = useState('');
  const [letterGenerating, setLetterGenerating] = useState(false);
  const [letterSavedDraft, setLetterSavedDraft] = useState(''); // tracks last saved
  // RC 1.49.1 P0-3 — true when `letterId` was hydrated from a
  // pre-existing letter via the pre-flight reuse helper. Flips to
  // false the moment the user clicks Regenerate so the badge does
  // not stick around past a fresh generation.
  // RC 1.49.x.RUN_ISOLATION — `letterReused` flag retired; letters
  // are per-run only.

  const [packName, setPackName] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  // RC 1.49.x Slice 4 — calm rotating reassurance copy for the
  // three long-running wizard actions. Uniform with AddJobDialog
  // (Slice 1B) and Analyse Fit rehydration (Slice 2B). Hooks return
  // null while the matching action flag is false, so the strip
  // simply doesn't render.
  const cvProgressMessage = useRotatingProgress(generating, 'cv');
  const letterProgressMessage = useRotatingProgress(letterGenerating, 'letter');
  const packProgressMessage = useRotatingProgress(creating, 'pack');
  // RC 1.49.x — Final Pack Confirmation modal gate. Renders between
  // the Step 4 "Create Application Pack" click and the actual POST.
  // No backend behaviour change; pure UI confirmation surface.
  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(null); // {title, body}
  const [previewLoading, setPreviewLoading] = useState(false);
  // RC 1.41.G4.D — Step 3 selected-CV Analyse-Fit state.
  //   bestFit: {score, sourceType, sourceId, label} | null
  //   step3FitRunning: boolean
  //   step3LastResult: {newScore, previousBest, replaced} | null
  const [bestFit, setBestFit] = useState(null);
  // RC 1.42 P0.2 — baseline-stale strip for Step 1. Surfaces a
  // previous Analyse Fit row that was scored against an OLD primary
  // CV (e.g. ikenna.pdf before swap). Display-only; the wizard
  // never re-runs baseline.
  const [baselineStale, setBaselineStale] = useState(null);
  // RC 1.42 P0.5 — full baseline-fit payload (incl. sub-scores),
  // used by Step 3's "What changed vs baseline" reasoning-delta
  // panel. Wire-only state — does not affect any other render.
  const [baselineFit, setBaselineFit] = useState(null);
  const [step3FitRunning, setStep3FitRunning] = useState(false);
  const [step3LastResult, setStep3LastResult] = useState(null);

  // RC 1.48.6 — Wizard state carry-forward. Shape:
  //
  //   fitSnapshot = {
  //     jobId:      string,    // guard for stale cross-job snapshots
  //     primaryCvId:string|null,// guard for stale cross-CV snapshots
  //     rows:       array,     // `/api/jobs/{id}/best-fit` all_cached
  //     fetchedAt:  number,    // Date.now() for freshness display
  //   } | null
  //
  // Prefetched once on bootstrap (right after `/baseline-fit`) and
  // refreshed after every successful Analyse Fit. Downstream
  // components (notably `Step3ScoreLandscape`) consume the snapshot
  // synchronously so advancing between wizard steps never shows a
  // 3–5 second loading skeleton — they hydrate from the carry-
  // forward and silently revalidate in the background.
  // Hard-invalidated when `jobId` or the primary CV id changes.
  const [fitSnapshot, setFitSnapshot] = useState(null);
  // RC 1.44 P0.4 — inferior-source / inferior-variant 409 modal
  // state. Populated by the 409 catch in `runAnalyseFitOnSelected`;
  // cleared on close or after the user picks any option.
  const [inferiorModal, setInferiorModal] = useState(null);  // { code, guidance } | null
  const [inferiorBusy,  setInferiorBusy]  = useState(null);  // 'stay' | 'continue' | null

  // RC 1.47.2.BASELINE-GOVERNANCE (2026-05-02) — version-aware
  // baseline modal state. Always re-fires when the canonical
  // baseline is stale (operator UX-A: trust-first, no extra
  // persistence, no session-level suppression).
  //
  //   baselineModal = null                          → not open
  //   baselineModal = {                             → open
  //     baseline_version, current_version,
  //     opts,                                       // captured Generate
  //                                                 // options to retry
  //                                                 // after decision
  //   }
  const [baselineModal, setBaselineModal] = useState(null);
  // RC 1.48.3 (2026-05-04) — operator soft-block UX. Backend retired
  // the blunt `fit_score < 60` reject; only a literal
  // `final_decision === 'reject'` from Source CV still surfaces a
  // 409 (`analyse_fit_reject`). When that happens, the wizard opens
  // a soft warning modal with "Generate Anyway" — confirming retries
  // the same generate call with `force_generate: true`. A pack-
  // canonical variant for THIS job suppresses the warning entirely
  // (we pre-emptively set `force_generate: true` on the first call).
  //
  //   rejectWarnModal = null                         → not open
  //   rejectWarnModal = {                            → open
  //     fit_score, cv_sub_score, threshold,
  //     final_decision, opts,
  //   }
  const [rejectWarnModal, setRejectWarnModal] = useState(null);

  // RC 1.48.6 — High-CV Value Guard warning modal. Fires BEFORE the
  // generate call when the Source CV NDOR verdict for this job is
  // already ≥80/100. Gives the user a choice to use the current
  // (already-strong) Source CV vs spend Credits on a regeneration
  // that may only deliver a presentation-level uplift. Confirmation
  // is persisted in sessionStorage keyed by jobId so the user is
  // never re-prompted for the same role within a session.
  //
  //   highCvWarnModal = null                         → not open
  //   highCvWarnModal = {                            → open
  //     source_cv_score: number,  // 0..100 baseline cv_sub_score
  //     opts: safeOpts,           // captured for resume on confirm
  //   }
  //
  // Backend contract: on "Generate anyway", `_runGenerateInner` is
  // re-invoked with `highCvConfirmed=true`, which sets
  // `high_cv_generate_confirmed: true` on the POST body — an audit-
  // only telemetry signal. The score-driven charge gate is strictly
  // source-vs-variant-delta-driven and is NEVER influenced by this
  // flag. See `routes/cv_variants.py::GenerateVariantRequest` and
  // `tests/test_rc_1_48_6_high_cv_value_guard.py` for the contract.
  const [highCvWarnModal, setHighCvWarnModal] = useState(null);
  // RC 1.41.G4.F — credits sufficiency state. Pre-flight check
  // gates every paid wizard action against
  // `included_usage_remaining + extra_usage_remaining`. When
  // insufficient, the paid endpoint is NEVER called; instead
  // <UsageExhaustedModal> opens with the canonical USAGE_EXHAUSTED
  // detail body so the user sees a clean modal with required +
  // available + a one-tap link to the Plan page. Backend 429s
  // are also routed here via handleCreditsError.
  const credits = useCredits();
  const [usageExhaustedOpen, setUsageExhaustedOpen] = useState(false);
  const [usageExhaustedDetail, setUsageExhaustedDetail] = useState(null);

  /**
   * Pre-flight credit check. Returns `true` when the action is
   * affordable (caller proceeds) and `false` when the modal has
   * been opened (caller must abort without calling the paid
   * endpoint).
   *
   * RC 1.49.5A P0-A3 (operator-locked, 2026-05-08) — credits-
   * loading race fix. Pre-fix this returned `true` optimistically
   * when `credits` data hadn't loaded yet, allowing a chargeable
   * backend request to fire during hydration. Post-fix it returns
   * `false` (no modal opened) so the caller aborts cleanly. The
   * step-component buttons are also disabled in the same window
   * via the `creditsLoading` prop, so this branch is mostly a
   * defensive last line of defence.
   */
  const ensureCreditsAvailable = useCallback((actionType, actionLabel) => {
    if (!credits || credits.loading) return false;
    const cost = getActionCost(credits.usageCosts, actionType);
    if (cost == null || cost === 0) return true;
    const available =
      Number(credits.includedUsageRemaining || 0) +
      Number(credits.extraUsageRemaining || 0);
    if (available >= cost) return true;
    setUsageExhaustedDetail({
      code: 'USAGE_EXHAUSTED',
      message: `You don't have enough Credits for ${actionLabel}.`,
      action_type: actionType,
      required_usage: cost,
      included_usage_remaining: Number(credits.includedUsageRemaining || 0),
      extra_usage_remaining: Number(credits.extraUsageRemaining || 0),
      plan_code: credits.planCode || null,
    });
    setUsageExhaustedOpen(true);
    return false;
  }, [credits]);

  // RC 1.49.5A P0-A3 — single source of truth for "credits not
  // hydrated yet". Wired into every chargeable step component so
  // their submit buttons disable + show "Loading your wallet…"
  // while credits is in flight. Free-action paths (job ingest,
  // recruiter email extract, source-CV upload) are NOT gated.
  const creditsLoading = !credits || !!credits.loading;

  // Hydration gate. Until the bootstrap effect has finished restoring
  // sessionStorage state into React state, the persist effect must
  // not write — otherwise React's render→effect ordering means the
  // persist effect runs first with the *initial* defaults and
  // clobbers the saved blob, breaking refresh-resume (Matrix #16).
  const hydratedRef = useRef(false);
  // RC 1.48.1 — abort controller for in-flight 202-path polling.
  const analysePollAbortRef = useRef(null);
  useEffect(() => {
    return () => {
      if (analysePollAbortRef.current) {
        analysePollAbortRef.current.abort();
        analysePollAbortRef.current = null;
      }
    };
  }, []);

  // ─── Bootstrap on open ───
  useEffect(() => {
    if (!open || !jobId) return;
    let alive = true;
    hydratedRef.current = false;

    const restored = loadWizardState(jobId);
    if (restored) {
      // RC 1.49.2 — wizard restructured from 5 → 4 steps (Job step
      // removed; Analyse Fit promoted to its own step). Migrate any
      // legacy `stepIdx` values written by the previous build:
      //   old 0 (Job)    → new 0 (CV)
      //   old 1 (CV)     → new 0 (CV)
      //   old 2 (Letter+Fit) → new 1 (Letter)
      //   old 3 (Review) → new 3 (Review)
      // Anything outside [0..3] is clamped to 0 to be safe.
      const rawIdx = restored.stepIdx;
      let migratedIdx = 0;
      if (typeof rawIdx === 'number') {
        if (rawIdx <= 1) migratedIdx = 0;        // Job + CV → CV
        else if (rawIdx === 2) migratedIdx = 1;  // Letter+Fit → Letter
        else if (rawIdx === 3) migratedIdx = 3;  // Review → Review
        else migratedIdx = Math.max(0, Math.min(3, rawIdx));
      }
      setStepIdx(migratedIdx);
      setSelectedCv(restored.selectedCv || null);
      setGeneratedResult(restored.generatedResult || null);
      setLetterId(restored.letterId || null);
      setLetterDraft(restored.letterDraft || '');
      setLetterSavedDraft(restored.letterDraft || '');
      setLetterTitle(restored.letterTitle || '');
      setPackName(restored.packName || '');
      setNotes(restored.notes || '');
      // RC 1.41.G4.D — restore best-fit pointer across page reloads so
      // the user never loses their strongest analyse-fit across the
      // wizard lifetime. Persisted by the saveWizardState effect.
      if (restored.bestFit) setBestFit(restored.bestFit);
      // RC 1.49.2 P0-FIN-LIFECYCLE-5 (2026-05-08) — rehydrate the
      // Step 3 result so the score panel + Next-button gate land
      // correctly on remount.
      if (restored.step3LastResult) setStep3LastResult(restored.step3LastResult);
    } else {
      setPackName(defaultPackName(job?.title, job?.company));
    }
    // Mark hydration complete on the next microtask so any state
    // updates queued above land before the persist effect runs.
    Promise.resolve().then(() => { if (alive) hydratedRef.current = true; });

    (async () => {
      // Load primary CV + bulk-score existing variants
      try {
        // RC 1.41 Phase D fix: there's no `/api/base-cvs` list route.
        // The canonical "who is the primary CV?" lookup is
        // `/api/profile/me` which already returns
        //   { profile: {...}, primary_cv: {id, original_filename, created_at} | null }
        // The full parsed_text is hydrated only when Step 4's
        // Preview button calls /api/base-cvs/{id} on demand.
        const me = await apiFetch('/api/profile/me');
        const primary = me?.primary_cv || null;
        if (!alive) return;
        setPrimaryCv(primary || null);

        if (primary && job?.description) {
          // RC 1.46 — Source CV deterministic keyword fetch retired
          // alongside the Step 1/Step 2 deterministic surfaces. The
          // wizard no longer reads `/keyword-fit/cv` on bootstrap.
          // Bulk variant scoring was already retired with RC 1.44.H4.
        }

        // Pre-existing Analyse-Fit baseline.
        // RC 1.42 P0.2 — switched from `/best-fit` to `/baseline-fit`.
        // The new endpoint returns ONLY rows scored against the user's
        // CURRENT primary CV (so a stale row against an older CV
        // never poses as the live baseline). It also surfaces a
        // separate `stale` block which Step 1 renders honestly.
        // Wizard Step 1 is now display-only — no charging, no
        // analyse-fit button, no progression block.
        try {
          const bf = await apiFetch(`/api/jobs/${jobId}/baseline-fit`);
          if (alive && bf) {
            setBaselineFit(bf);
            if (bf.baseline?.fit_score != null) {
              setFitScore(bf.baseline.fit_score);
              setFitDate(bf.baseline.evaluated_at);
              // Seed bestFit so Step 2/3 carry-forward starts from the
              // baseline. Restored sessionStorage state still wins if
              // its score is higher (Step 3 may have promoted a
              // variant since the last mount).
              setBestFit(prev => {
                const restoredScore = prev?.score ?? -1;
                if (bf.baseline.fit_score > restoredScore) {
                  return {
                    score: bf.baseline.fit_score,
                    sourceType: 'uploaded_cv',
                    sourceId:   bf.primary_cv?.id || null,
                    label:      bf.primary_cv?.filename || 'Source CV',
                  };
                }
                return prev;
              });
            }
            if (bf.stale) setBaselineStale(bf.stale);
          }
        } catch { /* non-fatal — Step 1 falls through to advisory copy */ }

        // RC 1.49.x.RUN_ISOLATION (operator-locked, 2026-05-06) —
        // pre-flight reuse REMOVED. The wizard NEVER hydrates a
        // historical CV variant or a historical cover letter on
        // mount. Each wizard run is an independent forward-only
        // flow: the user must explicitly click Generate to create a
        // tailored CV and Generate (or Skip) for the cover letter.
        //
        // SessionStorage-backed wizard state still survives a refresh
        // mid-run via `loadWizardState(jobId)` (the `restored` block
        // above) — that is THIS run's own state, not cross-run reuse.

        // RC 1.49.x — operator-locked 2026-05-07. In-flight CV
        // recovery: if the user navigated away DURING a Generate
        // CV call, the variant may have completed server-side
        // without the wizard ever reaching `setGeneratedResult`.
        // Look for the deterministic title-stamp marker and rebind
        // when found. Exact-match only — no "latest variant"
        // heuristic. No new endpoint, no charge implication.
        try {
          const marker = readInflightCvMarker(primary?.user_id, jobId);
          const restoredHasResult =
            !!(restored && restored.generatedResult);
          if (alive && marker && !restoredHasResult) {
            if (isInflightMarkerExpired(marker)) {
              clearInflightCvMarker(primary?.user_id, jobId);
              toast.info(
                "We couldn't automatically restore your last "
                + 'generated CV. Check Documents → Variants.',
              );
            } else {
              const found = await findVariantByTitleStamp(marker.title);
              if (alive && found) {
                const variantId = found.id || found.variant_id;
                const variantName = found.title || found.variant_name;
                if (variantId && variantName) {
                  // RC 1.49.2 P0-FIN-LIFECYCLE-1 / P0-6 (2026-05-08).
                  // Show the calm "verifying the score" copy first,
                  // then run the idempotent finalize-charge call so
                  // the recovered variant carries the same 20-field
                  // shape the happy path produces. Without this,
                  // `cv_sub_score_variant` / `fit_score_variant` /
                  // `charged` / `reason` are all undefined and the
                  // Step 2 panel renders the misleading "Tailoring
                  // not charged" copy even though the user WAS
                  // charged 10 NDOR Credits server-side. finalize-
                  // charge is idempotent on (variant_id) via the
                  // `charged_at` guard at routes/cv_variants.py:
                  // 1296, so calling it on a recovered variant
                  // either runs it once and stamps the row, or
                  // returns the persisted shape verbatim — no
                  // double-charge risk.
                  setGeneratedResult({
                    variant_id: variantId,
                    variant_name: variantName,
                    recovered_inflight: true,
                  });
                  setSelectedCv({
                    type: 'cv_variant',
                    id: variantId,
                    score: null,
                    name: variantName,
                    label: 'Tailored Variant',
                  });
                  clearInflightCvMarker(primary?.user_id, jobId);
                  toast.success(
                    'We restored your generated CV. Just verifying '
                    + 'the score…',
                  );
                  // Idempotent: server returns prior shape on replay.
                  try {
                    const charge = await apiFetch(
                      `/api/cv-variants/${variantId}/finalize-charge`,
                      { method: 'POST', body: { job_id: jobId } },
                    );
                    if (alive && charge) {
                      setGeneratedResult({
                        variant_id: variantId,
                        variant_name: variantName,
                        ...charge,
                      });
                      setSelectedCv((prev) => prev && prev.id === variantId ? {
                        ...prev,
                        score: charge.cv_sub_score_variant ?? null,
                      } : prev);
                    }
                    // RC 1.52 Phase 3A.4 — wizard recovery path was
                    // missing the post-finalize credits refresh.
                    // Without it the wallet pill stayed stale on
                    // tab-switch/refresh recovery flows.
                    if (alive && typeof credits?.refresh === 'function') {
                      try { credits.refresh(); } catch { /* noop */ }
                    }
                    // Recovery just resolved a dispatch — start the
                    // same cooldown the happy path uses.
                    if (alive) {
                      try {
                        startCooldown(cvVariantCooldownKey(primaryCv?.user_id, jobId));
                      } catch { /* noop */ }
                    }
                  } catch (e) {
                    // Fail-open: keep the recovered variant visible
                    // even if finalize-charge probe failed (e.g.
                    // transient network error). User can still
                    // navigate forward and the next step will
                    // surface the score via /best-fit hydration.
                    console.warn(
                      '[Wizard] recovered-variant finalize-charge '
                      + 'probe failed:', e,
                    );
                  }
                }
              }
            }
          }
        } catch { /* non-fatal */ }

        // RC 1.49.5 L-2 (operator-locked, 2026-05-08) — cover-letter
        // remount rebind. Mirror of the CV branch above. Only fires
        // when the restored state has no `letterId` AND a fresh
        // marker exists. Exact title-stamp match guards against
        // binding an unrelated letter.
        try {
          if (alive && !restored.letterId) {
            const letterMarker = readInflightLetterMarker(
              primary?.user_id, jobId,
            );
            if (
              letterMarker
              && !isInflightLetterMarkerExpired(letterMarker)
            ) {
              const found = await findLetterByTitleStamp(letterMarker.title);
              if (alive && found && found.id) {
                setLetterId(found.id);
                setLetterDraft(found.content_markdown || '');
                setLetterSavedDraft(found.content_markdown || '');
                setLetterTitle(found.title || letterMarker.title);
                clearInflightLetterMarker(primary?.user_id, jobId);
                clearIdempotencyKey(
                  primary?.user_id, jobId, 'cover_letter',
                );
                toast.success(
                  'We restored your generated cover letter.',
                );
              }
            }
          }
        } catch { /* non-fatal */ }
      } catch (err) {
        console.warn('[Wizard] bootstrap failed:', err);
      }
    })();
    return () => { alive = false; };
  }, [open, jobId, job?.title, job?.company, job?.description]);

  // ─── Persist state ───
  useEffect(() => {
    if (!open || !jobId) return;
    // Don't persist until bootstrap finished — otherwise the initial
    // defaults overwrite the blob we just restored from sessionStorage.
    if (!hydratedRef.current) return;
    saveWizardState(jobId, {
      stepIdx, selectedCv, generatedResult, letterId, letterDraft, letterTitle, packName, notes,
      bestFit,
      // RC 1.49.2 P0-FIN-LIFECYCLE-5 (2026-05-08) — persist the
      // Step 3 Analyse Fit result so a wizard remount surfaces the
      // score panel + enables the Next button without forcing the
      // user to click "Re-run Analyse Fit". The payload is small
      // (one row's worth of summary + reasoning_json). No charge
      // implication — purely UI state restoration.
      step3LastResult,
    });
  }, [open, jobId, stepIdx, selectedCv, generatedResult, letterId, letterDraft, letterTitle, packName, notes, bestFit, step3LastResult]);

  // ─── Actions ───
  // RC 1.41.G4.F — shared exhausted-from-429 callback for every
  // catch block. Receives the canonical USAGE_EXHAUSTED detail
  // body from `handleCreditsError`, mirrors it into local state,
  // and opens the same `<UsageExhaustedModal>` the pre-flight
  // path uses. Single source of truth so paid wizard flows look
  // identical whether the gate fires client-side or server-side.
  const onUsageExhaustedFromBackend = useCallback((detail) => {
    setUsageExhaustedDetail(detail || null);
    setUsageExhaustedOpen(true);
  }, []);

  // RC 1.48.6 — Wizard state carry-forward: `/best-fit` prefetch.
  // Called from wizard bootstrap AND after every successful Analyse
  // Fit run so downstream components that previously triggered a
  // synchronous `/best-fit` fetch on mount (notably
  // `Step3ScoreLandscape`) can hydrate from the snapshot and
  // revalidate silently. Fail-open — any network error leaves the
  // snapshot untouched so the legacy fetch path still runs.
  const refreshFitSnapshot = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await apiFetch(`/api/jobs/${jobId}/best-fit`);
      const rows = Array.isArray(res?.all_cached) ? res.all_cached : [];
      setFitSnapshot({
        jobId,
        primaryCvId: primaryCv?.id || null,
        rows,
        fetchedAt: Date.now(),
      });
    } catch { /* fail-open — downstream will fall back to its own fetch */ }
  }, [jobId, primaryCv?.id]);

  // Hard-invalidate the snapshot when the wizard's jobId OR primary
  // CV id changes (operator contract — RC 1.48.6). Downstream
  // consumers treat a `null` snapshot as "no carry-forward; hit the
  // network yourself" and the useEffect below re-warms it.
  useEffect(() => {
    setFitSnapshot((prev) => {
      if (!prev) return prev;
      const mismatch =
        prev.jobId !== jobId
        || prev.primaryCvId !== (primaryCv?.id || null);
      return mismatch ? null : prev;
    });
  }, [jobId, primaryCv?.id]);

  // Pre-warm the carry-forward as soon as the wizard has a jobId +
  // a primary CV in hand. Same-payload with the post-Analyse-Fit
  // call; fail-open.
  useEffect(() => {
    if (!open || !jobId || !primaryCv?.id) return;
    refreshFitSnapshot();
  }, [open, jobId, primaryCv?.id, refreshFitSnapshot]);

  // RC 1.49.2 P0-FIN-LIFECYCLE-4 (2026-05-08) — rehydrate Step 3
  // result from `/best-fit` data when the wizard mounts without a
  // persisted `step3LastResult` for the current selection. Without
  // this, a wizard remount after a successful Analyse Fit run
  // would render the empty-state advisory ("No fit analysis on
  // file for this CV yet") and disable Next, even though
  // `job_scores` already has the row. We only synthesise from a
  // row whose (source_type, source_id) match the currently-
  // selected CV — never auto-bind a stale or unrelated fit.
  // No new endpoint, no charge. Strictly UI hydration.
  useEffect(() => {
    if (!open || !hydratedRef.current) return;
    if (step3LastResult) return;             // already hydrated
    if (!fitSnapshot || !Array.isArray(fitSnapshot.rows)) return;
    if (!selectedCv?.id || !selectedCv?.type) return;
    if (selectedCv.type === 'base_cv') return; // baseline CV path skips Step 3
    const wantType = selectedCv.type === 'cv_variant' ? 'cv_variant' : 'uploaded_cv';
    const match = fitSnapshot.rows.find(
      (r) => r && r.source_type === wantType && r.source_id === selectedCv.id,
    );
    if (!match) return;
    const fitVal = match.fit_score;
    const newScore = (typeof fitVal === 'number' && fitVal <= 1)
      ? Math.round(fitVal * 100)
      : fitVal;
    setStep3LastResult({
      newScore,
      previousLatest: null,
      replaced: false,
      analysis: match.reasoning_json || null,
      sourceType: match.source_type,
      sourceId:   match.source_id,
      // Cached row → not "explicitly run this wizard run" per the
      // RUN_ISOLATION operator contract, so scoreId stays null
      // (mirrors line 1873–1879 cached-fit policy).
      scoreId: null,
      scoreRow: {
        score_id:        match.score_id ?? null,
        fit_score:       fitVal ?? null,
        final_decision:  match.final_decision ?? null,
        salary_score:    match.salary_score ?? null,
        seniority_score: match.seniority_score ?? null,
        location_score:  match.location_score ?? null,
        strategic_score: match.strategic_score ?? null,
        risk_score:      match.risk_score ?? null,
        evaluated_at:    match.evaluated_at ?? null,
        reasoning_json:  match.reasoning_json ?? null,
      },
      cvLabel: match.label || null,
      _hydratedFromSnapshot: true,
    });
    // RC 1.49.5 L-3 — clear the remount-recovery marker as soon as
    // the fit row this marker pointed at becomes hydrated. Allows
    // the running-flag spinner in the parallel useEffect below to
    // settle on the very next poll tick.
    try {
      const marker = readInflightFitMarker(primaryCv?.user_id, jobId);
      if (
        marker
        && marker.sourceType === match.source_type
        && marker.sourceId === match.source_id
      ) {
        clearInflightFitMarker(primaryCv?.user_id, jobId);
      }
    } catch { /* non-fatal */ }
  }, [open, fitSnapshot, selectedCv?.id, selectedCv?.type, step3LastResult]);

  // RC 1.49.5 L-3 (operator-locked, 2026-05-08) — Analyse Fit
  // remount poll-resumption.
  //
  // If the wizard remounts while an Analyse Fit was in flight, the
  // poll AbortController fired on unmount but the LLM call lands
  // server-side regardless. This effect:
  //   1. Reads the in-flight marker for this (user, job).
  //   2. If marker exists AND no `step3LastResult` matches yet:
  //        - sets `step3FitRunning=true` so Next stays disabled
  //        - polls `refreshFitSnapshot()` every 8 s for up to
  //          100 s (mirrors the timeoutRecovery budget).
  //        - the existing P0-4 hydration effect (above) auto-
  //          binds `step3LastResult` the moment the matching
  //          row appears in /best-fit.
  //        - on success OR budget exhaustion, clears the marker
  //          and the running flag.
  //
  // Hard rails:
  //   - Read-only (GET /best-fit only). Never re-dispatches
  //     /analyze-fit. Never charges.
  //   - Bound by a single useEffect lifecycle so a fast remount
  //     can't fire two concurrent loops.
  useEffect(() => {
    if (!open || !hydratedRef.current) return undefined;
    if (!jobId || !primaryCv?.user_id) return undefined;
    const marker = readInflightFitMarker(primaryCv.user_id, jobId);
    if (!marker) return undefined;
    // If we already have a result that matches the marker's source,
    // the marker is stale but resolved — clear and exit.
    if (
      step3LastResult
      && step3LastResult.sourceType === marker.sourceType
      && step3LastResult.sourceId === marker.sourceId
    ) {
      clearInflightFitMarker(primaryCv.user_id, jobId);
      return undefined;
    }
    let alive = true;
    let attempts = 0;
    const MAX_ATTEMPTS = 10;
    const INTERVAL_MS = 8000;
    setStep3FitRunning(true);
    const tick = async () => {
      if (!alive) return;
      attempts += 1;
      try { await refreshFitSnapshot(); } catch { /* non-fatal */ }
      if (!alive) return;
      // P0-4 hydration runs on the next render via the fitSnapshot
      // dependency. We just need to know whether it landed.
      // Read the freshly-fetched marker again — if it was cleared
      // by the finally block of a fresh dispatch, we should bail.
      const stillMarker = readInflightFitMarker(primaryCv.user_id, jobId);
      if (!stillMarker) {
        setStep3FitRunning(false);
        // RC 1.52 Phase 3B.1 — marker cleared by P0-4's fitSnapshot
        // hydrator means a /best-fit row has landed. Refresh wallet
        // so the post-charge balance is visible without a hard
        // reload (mirrors CvSection Phase 3A.4 recovery refresh).
        try { credits?.refresh?.(); } catch { /* noop */ }
        return;
      }
      if (attempts >= MAX_ATTEMPTS) {
        clearInflightFitMarker(primaryCv.user_id, jobId);
        if (alive) setStep3FitRunning(false);
        return;
      }
      setTimeout(tick, INTERVAL_MS);
    };
    setTimeout(tick, INTERVAL_MS);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId, primaryCv?.user_id, hydratedRef.current]);

  // RC 1.42 P0.2 — `runAnalyseFit` (the Step 1 paid Analyse Fit on
  // the primary CV) was REMOVED. Baseline Analyse Fit lives on the
  // Jobs page exclusively. The wizard never charges for baseline.
  // The Step 3 variant analyser (`runAnalyseFitOnSelected`, below)
  // is preserved because that targets generated CVs / variants /
  // non-primary sources — i.e. optimisation, not baseline.

  // RC 1.41.G4.D — Analyse Fit on the SELECTED CV (Source / Recommended /
  // Generated alike). Best-fit carry-forward: replace the bestFit
  // pointer only when the new score is strictly higher than the
  // current best. Skipping or running a lower-score CV never
  // downgrades the user's strongest fit for this (user, job).
  //
  // RC 1.44 P0.4 — backend may now return HTTP 409 with code
  // INFERIOR_SOURCE / INFERIOR_VARIANT. We surface the
  // <InferiorSourceModal> so the user can pick: stay-with-source /
  // choose-stronger / generate-new / continue-anyway. The
  // continue-anyway branch re-calls this fn with
  // `forceOverride=true` to bypass R2/R3 server-side.
  const runAnalyseFitOnSelected = async ({
    sourceTypeOverride, sourceIdOverride, forceOverride = false,
  } = {}) => {
    const targetType =
      sourceTypeOverride
      ?? (selectedCv?.type === 'cv_variant' ? 'cv_variant' : 'uploaded_cv');
    const targetId = sourceIdOverride ?? selectedCv?.id;

    if (!targetId) {
      toast.error('Select a CV in Step 2 first.');
      return;
    }
    // ── RC 1.49.x.TRANSIENT_SCORING Stage 2 (operator-locked,
    //    2026-05-06) ────────────────────────────────────────────
    // 30-minute sessionStorage duplicate-run guard — variant-only.
    // Variant Analyse Fit is now transient (no DB row), so a
    // double-click or second wizard return within the same browser
    // session would otherwise charge the user twice for an
    // identical run. The guard reuses the cached transient result
    // for up to VARIANT_FIT_GUARD_TTL_MS. NOT persisted server-side.
    // Source-CV (`uploaded_cv`) runs are NOT guarded here — that
    // path runs through the canonical baseline / refresh-baseline
    // contract which has its own server-side staleness logic.
    const VARIANT_FIT_GUARD_TTL_MS = 30 * 60 * 1000; // 30 min
    const guardKey = (targetType === 'cv_variant')
      ? `variantFitGuard:${primaryCv?.user_id || ''}:${jobId}:${targetId}`
      : null;
    if (guardKey) {
      try {
        const raw = sessionStorage.getItem(guardKey);
        if (raw) {
          const cached = JSON.parse(raw);
          const cachedAt = Number(cached?.cachedAt) || 0;
          if (cachedAt && (Date.now() - cachedAt) < VARIANT_FIT_GUARD_TTL_MS && cached?.payload) {
            const r = cached.payload;
            const fitVal = r?.analysis?.fit_score ?? r?.score?.fit_score ?? null;
            const newScore = (fitVal != null && fitVal <= 1) ? Math.round(fitVal * 100) : fitVal;
            setStep3LastResult({
              newScore,
              previousLatest: bestFit?.score ?? null,
              replaced: newScore != null,
              analysis: r?.analysis || null,
              sourceType: targetType,
              sourceId:   targetId,
              // No DB row → no scoreId. Wizard's createPack still
              // populates pack_snapshot.fit from scoreRow below.
              scoreId: null,
              scoreRow: r?.score ? {
                score_id:        r.score.id ?? null,
                fit_score:       r.score.fit_score ?? null,
                final_decision:  r.score.final_decision ?? null,
                salary_score:    r.score.salary_score ?? null,
                seniority_score: r.score.seniority_score ?? null,
                location_score:  r.score.location_score ?? null,
                strategic_score: r.score.strategic_score ?? null,
                risk_score:      r.score.risk_score ?? null,
                evaluated_at:    r.score.evaluated_at ?? r.score.created_at ?? null,
                reasoning_json:  r.score.reasoning_json ?? r.analysis ?? {},
              } : null,
            });
            toast.info('Reused your last variant Analyse Fit (within 30 min).');
            return;
          }
        }
      } catch { /* fall through to live call */ }
    }
    if (!ensureCreditsAvailable('analyze_fit', 'Analyse Fit on selected CV')) return;
    setStep3FitRunning(true);
    // RC 1.49.5 L-3 (operator-locked, 2026-05-08) — write the
    // remount-recovery marker so a wizard unmount during the LLM
    // call (or a tab background) can be recovered by the bootstrap
    // poll-resumption useEffect (no charge — read-only /best-fit
    // probe + cache-hit semantics).
    writeInflightFitMarker(primaryCv?.user_id, jobId, {
      sourceType: targetType,
      sourceId:   targetId,
    });
    // RC 1.57 Generation Session Guard — get-or-mint the session-
    // scoped idempotency key for this (user, job, 'analyse_fit')
    // tuple. A click that lands while a session is still active
    // (e.g. a remount of the wizard after a tab switch) reuses the
    // SAME key — the backend's `credit_ledger.idempotency_key`
    // partial unique index then dedupes the duplicate POST at the DB.
    const _af_session = getOrMintSession(
      primaryCv?.user_id, jobId, 'analyse_fit',
    );
    const _af_idempotency_key = _af_session.idempotencyKey;
    try {
      // RC 1.48.1 — abort prior in-flight polling (e.g. user clicked
      // Analyse Fit twice in quick succession). The wizard is its own
      // lifecycle so we abort on unmount + on supersede.
      if (analysePollAbortRef.current) {
        analysePollAbortRef.current.abort();
      }
      analysePollAbortRef.current = new AbortController();
      const r = await runAnalyzeFitWithPolling(jobId, {
        source_type:     targetType,
        source_id:       targetId,
        idempotency_key: _af_idempotency_key,
        ...(forceOverride ? { force_override: true } : {}),
      }, {
        signal: analysePollAbortRef.current.signal,
        onRecoveryState: (s) => {
          if (s === 'checking') {
            toast.info("This is taking longer than usual. We're checking your results...");
          } else if (s === 'recovered') {
            toast.success('Your fit analysis was completed in the background.');
          }
        },
      });
      // RC 1.57 — terminal success: lock the session for the
      // stabilisation window so any visibility / remount event in
      // the next 30 s reuses the same key (no fresh charge).
      markTerminalSuccess(primaryCv?.user_id, jobId, 'analyse_fit');
      const fitVal = r?.analysis?.fit_score ?? r?.score?.fit_score ?? null;
      const newScore = (fitVal != null && fitVal <= 1) ? Math.round(fitVal * 100) : fitVal;
      // RC 1.47.CANONICAL-LATEST-RUN-GOVERNANCE — operator P0:
      //   "Latest Valid Run Wins". The wizard now binds whatever
      //   was just run — no `newScore > previousBest` gating.
      //   The internal variable `bestFit` is retained for diff
      //   minimality; semantically it is now "the LATEST fit for
      //   this job", never "historical best". UI copy has been
      //   updated to say "Latest" everywhere a user sees it.
      const previousLatest = bestFit?.score ?? null;

      // Latest always replaces; historical-best promotion removed.
      let replaced = newScore != null;
      if (replaced) {
        setBestFit({
          score: newScore,
          sourceType: targetType,
          sourceId:   targetId,
          label: selectedCv?.label || (targetType === 'cv_variant' ? 'CV variant' : 'Source CV'),
        });
      }
      setStep3LastResult({
        newScore,
        previousLatest,
        replaced,
        analysis: r?.analysis || null,
        // RC 1.49.x.PFB — record source_type + source_id so the
        // Step 4 createPack pre-flight can verify the fit row that
        // exists locally ALSO matches the currently-selected CV
        // (variant or source). If the user changes their CV after
        // running fit, the guard re-fires.
        sourceType: targetType,
        sourceId:   targetId,
        // RC 1.49.x.RUN_ISOLATION (2026-05-06) — capture the
        // `job_scores.id` of the row just produced. Step 4
        // `createPack` sends this exact id as `analyse_fit_id`,
        // binding the pack to THIS run's analysis row only. Cached
        // hits (`r.billing.cached === true`) deliberately do NOT
        // populate `scoreId`: if the wizard merely re-fetched a
        // pre-existing row, the user did not "explicitly run
        // Analyse Fit in this run" per the operator contract, so
        // the pack is created with `analyse_fit_id=null` (Case C).
        scoreId: (
          r?.billing?.cached === true
            ? null
            : (r?.score?.id ?? null)
        ),
        // RC 1.49.x.TRANSIENT_SCORING Stage 1 (2026-05-06) — full
        // score row payload captured for the pack snapshot.
        // Pack rendering reads from `pack.pack_snapshot` only;
        // this object becomes `pack_snapshot.fit` at create time.
        scoreRow: r?.score ? {
          score_id:        r.score.id ?? null,
          fit_score:       r.score.fit_score ?? null,
          final_decision:  r.score.final_decision ?? null,
          salary_score:    r.score.salary_score ?? null,
          seniority_score: r.score.seniority_score ?? null,
          location_score:  r.score.location_score ?? null,
          strategic_score: r.score.strategic_score ?? null,
          risk_score:      r.score.risk_score ?? null,
          evaluated_at:    r.score.evaluated_at ?? r.score.created_at ?? null,
          reasoning_json:  r.score.reasoning_json ?? r.analysis ?? {},
        } : null,
      });
      track('analyze_fit_run', {
        cached: !!r?.billing?.cached,
        new_score: newScore,
        replaced,
        source_type: targetType,
      });
      fireFirstActionOnce('analyze_fit');
      // RC 1.49.x.TRANSIENT_SCORING Stage 2 — cache the variant
      // transient result in sessionStorage so a duplicate run
      // within 30 minutes reuses it instead of re-charging.
      if (guardKey && newScore != null) {
        try {
          sessionStorage.setItem(guardKey, JSON.stringify({
            cachedAt: Date.now(),
            payload: r,
          }));
        } catch { /* sessionStorage may be unavailable in private mode */ }
      }
      // RC 1.49.4 Tier 1 — fit run mutates the job's score data
      // surfaced on Jobs page badges + Pack canonical fit. Drop
      // the per-job cache so the next mount of those surfaces
      // fetches fresh.
      if (jobId) cacheInvalidate(`jobs:by-id:${jobId}`);
      cacheInvalidate(['jobs:list']);
      toast.success(
        r?.billing?.cached
          ? 'Existing Analyse Fit loaded.'
          : 'Analyse Fit complete — your latest score for this job.',
      );
      credits?.refresh?.();
      // RC 1.52 Phase 3B.1 — start the 5-min Analyse Fit cooldown on
      // a confirmed successful dispatch (paid or cached). Scoped to
      // (user, job, source) so analysing a fresh variant later
      // doesn't get blocked by the Source CV cooldown.
      try {
        startCooldown(analyseFitCooldownKey(primaryCv?.user_id, jobId, targetId));
      } catch { /* noop */ }
      // RC 1.48.6 — refresh the wizard carry-forward so Step 3's
      // Score Landscape hydrates instantly on step advance instead
      // of showing a 3–5s skeleton while `/best-fit` re-fetches.
      refreshFitSnapshot();
      // RC 1.57 Generation Session Guard — hydration is now stable
      // (best-fit refresh fired, success card bound). Mark the
      // session stable and release it only if safe to do so.
      markHydrationStable(primaryCv?.user_id, jobId, 'analyse_fit');
      releaseSessionIfStable(primaryCv?.user_id, jobId, 'analyse_fit');
      // Successful run after a 409 — close the modal.
      setInferiorModal(null);
    } catch (err) {
      // RC 1.48.1 — abort signals from the polling helper bubble
      // up as DOMException AbortError when the wizard unmounts or
      // the user supersedes the call. Swallow silently.
      if (err?.name === 'AbortError') {
        return;
      }
      // RC 1.44 P0.4 — surface inferior-source / variant modal.
      const code = err?.data?.detail?.code;
      if (err?.status === 409 && (code === 'INFERIOR_SOURCE' || code === 'INFERIOR_VARIANT')) {
        setInferiorModal({
          code,
          guidance: err.data.detail.guidance || {},
        });
        return;
      }
      if (handleCreditsError(err, { action: 'Analyse Fit', onExhausted: onUsageExhaustedFromBackend, refreshCredits: credits?.refresh })) return;
      toast.error(err?.data?.detail?.message || err?.message || 'Analyse Fit failed.');
    } finally {
      setStep3FitRunning(false);
      setInferiorBusy(null);
      // RC 1.49.5 L-3 — clear remount-recovery marker on either
      // success or error. The user has a fresh outcome (or an
      // explicit error toast) and any future click is a deliberate
      // re-run, not a recovery.
      clearInflightFitMarker(primaryCv?.user_id, jobId);
    }
  };

  // RC 1.44 P0.4 — InferiorSourceModal action handlers.
  const handleStayWithSourceCv = async () => {
    setInferiorBusy('stay');
    const primaryId = inferiorModal?.guidance?.primary_cv_id;
    if (!primaryId) {
      toast.error('Could not resolve your primary Source CV.');
      setInferiorBusy(null);
      return;
    }
    await runAnalyseFitOnSelected({
      sourceTypeOverride: 'uploaded_cv',
      sourceIdOverride:   primaryId,
    });
  };
  const handleChooseStrongerCv = () => {
    // Close modal; return user to the CV chooser (now step 0).
    setInferiorModal(null);
    setStepIdx(0);
  };
  const handleGenerateNewCv = () => {
    setInferiorModal(null);
    // The CV chooser (now step 0) hosts the Generate flow; we open
    // it implicitly by switching steps. Its existing Generate CTA
    // does the rest.
    setStepIdx(0);
  };
  const handleContinueAnyway = async () => {
    setInferiorBusy('continue');
    await runAnalyseFitOnSelected({ forceOverride: true });
  };

  // RC 1.52 Phase 3A.DUP — system-name minting now lives in
  // `@/lib/titleStamps`. The legacy per-second + jobId-fragment
  // helpers (`_stampNow` / `_shortJobSuffix`) are retired here in
  // favour of the shared, collision-proof helpers imported at the
  // top of the file.

  // RC 1.41.G4.G — Generate-CV configuration sheet state. Step 2's
  // "Generate" button now opens this sheet first; the actual
  // /api/cv-variants/generate call is deferred until the user
  // confirms inside the sheet with their template/tone/length
  // choices.
  const [generateSheetOpen, setGenerateSheetOpen] = useState(false);
  const openGenerateSheet = () => {
    if (!primaryCv?.id) { toast.error('No Source CV.'); return; }
    if (!job?.description?.trim()) { toast.error('Job has no description text.'); return; }
    if (!ensureCreditsAvailable('cv_improve', 'Generate CV')) return;
    setGenerateSheetOpen(true);
  };

  // Short, readable suffix appended to the variant_name so users can
  // tell their Executive · Strategic · One-page run apart from their
  // Standard · Analytical · Flexible run for the same job in
  // Documents → CV Variants. Skipped when all three knobs are at
  // defaults — keeps legacy variant naming clean.
  const _variantSuffix = (opts) => {
    const isDefault =
      opts.template_type     === GEN_CV_DEFAULTS.template_type &&
      opts.tone_type         === GEN_CV_DEFAULTS.tone_type &&
      opts.length_preference === GEN_CV_DEFAULTS.length_preference;
    if (isDefault) return '';
    const tplLbl    = TEMPLATES.find(t => t.value === opts.template_type)?.label     || opts.template_type;
    const toneLbl   = TONES    .find(t => t.value === opts.tone_type)?.label         || opts.tone_type;
    const lengthLbl = LENGTHS  .find(t => t.value === opts.length_preference)?.label || opts.length_preference;
    return ` · ${tplLbl} · ${toneLbl} · ${lengthLbl}`;
  };

  // RC 1.47.2 — internal helper. Performs Generate + finalize-charge
  // with optional `refreshDecision` ("refreshed" | "declined" | null).
  // Centralised so the BaselineRefreshModal callbacks can re-invoke
  // the same flow with an explicit decision after the user responds.
  // RC 1.48.3 — additionally forwards `force_generate` so the soft
  // reject-gate override (operator "Generate Anyway") AND the pack-
  // canonical pre-emptive bypass both flow through one path.
  // RC 1.48.6 — additionally forwards `highCvConfirmed` so the
  // High-CV Value Guard "Generate anyway" confirmation reaches the
  // backend as `high_cv_generate_confirmed=true` for audit/telemetry.
  const _runGenerateInner = async (
    safeOpts,
    refreshDecision = null,
    forceGenerate = false,
    highCvConfirmed = false,
  ) => {
    const variantTitle = mintCvVariantTitle({
      role:   job?.title,
      suffix: _variantSuffix(safeOpts),
    });
    // RC 1.49.x — operator-locked 2026-05-07. Write the in-flight
    // marker BEFORE the LLM call so a wizard remount (tab close /
    // route navigation / mobile suspend) can rebind the variant
    // by deterministic title-stamp match. NO billing implication —
    // the marker is sessionStorage-only and never charges credits.
    writeInflightCvMarker(primaryCv?.user_id, jobId, variantTitle);
    // RC 1.49.1 P0-FIN (2026-05-08) — financial idempotency.
    // Get-or-mint a UUID per (user, job, action) so retries after a
    // 30s ingress 504 reuse the SAME key. The backend's
    // `credits_svc.charged(idempotency_key=…)` adoption (prefixed
    // `cv_variant:` server-side) plumbs into the existing
    // `(user_id, idempotency_key)` partial unique index in
    // `credit_ledger` (migration 004), making `charge_credits`
    // return the prior reservation row instead of debiting again.
    const idempotencyKey = getOrMintIdempotencyKey(
      primaryCv?.user_id, jobId, 'cv_variant',
    );
    // RC 1.49.1 P0-A — wrap the long-running LLM call so a 30s
    // ingress proxy 504 doesn't double-charge the user. If the
    // backend completes in the background the recovery loop will
    // surface the persisted variant.
    const variant = await withTimeoutRecovery({
      kind:  'cv',
      jobId,
      onRecoveryState: (s) => {
        if (s === 'checking') {
          toast.info("This is taking longer than usual. We're checking your results...");
        } else if (s === 'recovered') {
          toast.success('Your result was completed successfully.');
        } else if (s === 'failed') {
          // RC 1.49.1 P0-FIN (2026-05-08) — calm operational
          // guidance when the recovery loop exhausts. Steers the
          // user away from a reflexive second click while the
          // idempotency key (still persisted in sessionStorage)
          // would protect them either way.
          toast.warning(
            "We're unable to confirm the result reached you. Before clicking Generate again, please open Documents → CV Variants — your document may already be there.",
            { duration: 9000 },
          );
        }
      },
      generate: () => apiFetch('/api/cv-variants/generate', {
        method: 'POST',
        body: {
          base_cv_id: primaryCv.id,
          job_id: jobId,
          job_text: job.description,
          title: variantTitle,
          template_type:     safeOpts.template_type,
          tone_type:         safeOpts.tone_type,
          length_preference: safeOpts.length_preference,
          additional_cv_context: safeOpts.additional_cv_context || '',
          // RC 1.48.3 — surface the soft-override decision to backend.
          force_generate: !!forceGenerate,
          // RC 1.48.6 — surface the High-CV Value Guard confirmation
          // to the backend. Audit-only: charge gate is score-driven.
          high_cv_generate_confirmed: !!highCvConfirmed,
          // RC 1.49.1 P0-FIN — financial idempotency for retries.
          idempotency_key: idempotencyKey,
        },
      }),
    });
    const variantId = variant?.variant_id || variant?.id;
    if (!variantId) throw new Error('Variant id missing in response.');
    const finalizeBody = { job_id: jobId };
    if (refreshDecision) finalizeBody.refresh_decision = refreshDecision;
    const charge = await apiFetch(`/api/cv-variants/${variantId}/finalize-charge`, {
      method: 'POST', body: finalizeBody,
    });
    // RC 1.49.1 P0-FIN — drop the cached idempotency key on confirmed
    // success so the next legitimate Generate click mints a fresh one.
    clearIdempotencyKey(primaryCv?.user_id, jobId, 'cv_variant');
    return { variant, variantId, charge };
  };

  const generateVariant = async (opts) => {
    if (!primaryCv?.id) { toast.error('No Source CV.'); return; }
    if (!job?.description?.trim()) { toast.error('Job has no description text.'); return; }
    if (!ensureCreditsAvailable('cv_improve', 'Generate CV')) return;
    // Use defaults if the caller didn't pass options (back-compat
    // for any legacy invocation; the sheet always passes a full
    // triple).
    const safeOpts = {
      template_type:     opts?.template_type     || GEN_CV_DEFAULTS.template_type,
      tone_type:         opts?.tone_type         || GEN_CV_DEFAULTS.tone_type,
      length_preference: opts?.length_preference || GEN_CV_DEFAULTS.length_preference,
      // RC 1.41 P0.1.D — propagate the candidate's free-text addendum
      // through to the body builder. Empty string when the sheet
      // wasn't used (legacy invocation) so the backend treats it as
      // "not provided" and emits the byte-identical pre-RC-1.41
      // prompt.
      additional_cv_context: (opts?.additional_cv_context || '').trim(),
    };

    // RC 1.48.6 — High-CV Value Guard pre-flight. If the Source CV
    // for this job already scores ≥ HIGH_CV_THRESHOLD and the user
    // has not yet acknowledged the warning this session, open the
    // modal and capture `safeOpts` so the confirm handler can resume
    // the same flow. This is a pure UX pre-flight — the authoritative
    // billing gate remains server-side in finalize-charge.
    const _sourceCvScore = baselineFit?.baseline?.sub_scores?.cv_sub_score;
    let _highCvAlreadyConfirmed = false;
    try {
      _highCvAlreadyConfirmed =
        sessionStorage.getItem(highCvSessionKey(jobId)) === '1';
    } catch { /* sessionStorage unavailable — fail open to warning */ }
    if (
      typeof _sourceCvScore === 'number'
      && _sourceCvScore >= HIGH_CV_THRESHOLD
      && !_highCvAlreadyConfirmed
    ) {
      setHighCvWarnModal({
        source_cv_score: _sourceCvScore,
        opts: safeOpts,
      });
      return; // Modal callbacks resume the flow.
    }

    await _generateVariantAfterHighCvCheck(safeOpts, false);
  };

  // RC 1.48.6 — shared continuation used by both the default
  // generate path and the High-CV "Generate anyway" confirm handler.
  // `highCvConfirmed` is forwarded to the backend as an audit-only
  // telemetry signal (see `_runGenerateInner`).
  const _generateVariantAfterHighCvCheck = async (safeOpts, highCvConfirmed) => {
    // RC 1.47.2 — pre-flight baseline-version check. If stale,
    // open the BaselineRefreshModal BEFORE we kick off the LLM
    // generation call. This avoids paying the wall-clock cost of
    // generating a CV against a baseline the user might decline.
    try {
      const status = await apiFetch(`/api/jobs/${jobId}/baseline-status`);
      if (status?.baseline_status === 'stale_refresh_available') {
        setBaselineModal({
          baseline_version: status.baseline_version,
          current_version:  status.current_version,
          opts: safeOpts,
        });
        return; // Modal callbacks resume the flow.
      }
    } catch (e) {
      // Pre-flight failure should NOT block the user. We fall
      // through to the normal flow and rely on the 409 short-
      // circuit inside finalize-charge as the defence-in-depth
      // retry path.
      console.warn('[RC 1.47.2] baseline-status pre-flight failed:', e?.message || e);
    }

    setGenerating(true);
    try {
      // 1+2. Generate + finalize-charge. RC 1.47.2: 409
      // BASELINE_VERSION_STALE handled by re-opening the modal.
      // RC 1.48.3: pack-canonical pre-emptive bypass — when an
      // application_pack already exists for this job (any pack_status
      // backed by a tailored variant id), the user has already
      // committed to generation against this role; we set
      // force_generate=true on the first call so the legacy
      // `analyse_fit_reject` path never surfaces a redundant warning
      // for jobs the user is actively packing.
      //
      // RC 1.48.5A hotfix — the previous implementation referenced an
      // undefined `packs` local, crashing with `packs is not defined`
      // and blocking the entire generation flow. Fetch the pack
      // existence boolean from the API scoped to THIS job — cheap,
      // read-only, and pinned by the Jest regression test added
      // alongside this fix.
      let packExists = false;
      try {
        const res = await apiFetch(
          `/api/application-packs?job_id=${encodeURIComponent(jobId)}&page_size=1`,
        );
        const items = Array.isArray(res) ? res : (res?.items || []);
        packExists = items.some(
          (p) => p?.job_id === jobId && !!p?.cv_variant_id,
        );
      } catch { /* fail open — worst case, warn-once path fires */ }
      let result;
      try {
        result = await _runGenerateInner(safeOpts, null, packExists, highCvConfirmed);
      } catch (e) {
        if (e?.status === 409 && e?.data?.detail?.code === 'BASELINE_VERSION_STALE') {
          // Modal short-circuit. Capture the versions and re-fire.
          setBaselineModal({
            baseline_version: e.data.detail.baseline_version,
            current_version:  e.data.detail.current_version,
            opts: safeOpts,
          });
          return;
        }
        // RC 1.48.3 — soft reject-gate. Open the warning modal and
        // let the user decide. No retry happens until they confirm.
        if (e?.status === 409 && e?.data?.detail?.error === 'analyse_fit_reject') {
          setRejectWarnModal({
            fit_score:       e.data.detail.fit_score ?? null,
            cv_sub_score:    e.data.detail.cv_sub_score ?? null,
            threshold:       e.data.detail.threshold ?? null,
            final_decision:  e.data.detail.final_decision ?? null,
            opts: safeOpts,
          });
          return;
        }
        throw e;
      }
      const _cvCost = getActionCost(credits.usageCosts, 'cv_improve');
      const _cvLabel = _cvCost == null ? 'Credits' : `${_cvCost} ${_cvCost === 1 ? 'Credit' : 'Credits'}`;
      _applyGenerateSuccess(result, _cvLabel);
    } catch (err) {
      if (handleCreditsError(err, { action: 'Generate CV', onExhausted: onUsageExhaustedFromBackend, refreshCredits: credits?.refresh })) return;
      // RC 1.49.x Slice 4 — humanise generic 5xx so the user
      // doesn't see "Internal server error". USAGE_EXHAUSTED /
      // FREE_DAILY_ANALYZE_LIMIT_REACHED return null and fall
      // through to the existing legacy fallback for back-compat.
      const friendly = humaniseWizardError(err, 'cv');
      toast.error(friendly || err?.data?.detail?.message || err?.message || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  // RC 1.47.2 — BaselineRefreshModal callbacks. Each one resumes the
  // captured Generate flow with an explicit `refresh_decision` value
  // so the backend takes the correct branch on finalize-charge.

  const handleBaselineRefresh = async () => {
    if (!baselineModal) return;
    const captured = baselineModal;
    try {
      await apiFetch(`/api/jobs/${jobId}/refresh-baseline`, { method: 'POST' });
      toast.success('Baseline refreshed — no Credits charged.');
    } catch (e) {
      toast.error(e?.data?.detail?.message || e?.message || 'Baseline refresh failed.');
      return; // leave modal open so user can retry / cancel
    }
    setBaselineModal(null);
    setGenerating(true);
    try {
      const result = await _runGenerateInner(captured.opts, 'refreshed');
      // Re-use the post-generation handler by re-calling generateVariant
      // would re-run the pre-flight; instead inline the success block.
      const _cvCost = getActionCost(credits.usageCosts, 'cv_improve');
      const _cvLabel = _cvCost == null ? 'Credits' : `${_cvCost} ${_cvCost === 1 ? 'Credit' : 'Credits'}`;
      _applyGenerateSuccess(result, _cvLabel);
    } catch (err) {
      if (handleCreditsError(err, { action: 'Generate CV', onExhausted: onUsageExhaustedFromBackend, refreshCredits: credits?.refresh })) return;
      const friendly = humaniseWizardError(err, 'cv');
      toast.error(friendly || err?.data?.detail?.message || err?.message || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  const handleBaselineDecline = async () => {
    if (!baselineModal) return;
    const captured = baselineModal;
    setBaselineModal(null);
    setGenerating(true);
    try {
      const result = await _runGenerateInner(captured.opts, 'declined');
      const _cvCost = getActionCost(credits.usageCosts, 'cv_improve');
      const _cvLabel = _cvCost == null ? 'Credits' : `${_cvCost} ${_cvCost === 1 ? 'Credit' : 'Credits'}`;
      _applyGenerateSuccess(result, _cvLabel);
    } catch (err) {
      if (handleCreditsError(err, { action: 'Generate CV', onExhausted: onUsageExhaustedFromBackend, refreshCredits: credits?.refresh })) return;
      const friendly = humaniseWizardError(err, 'cv');
      toast.error(friendly || err?.data?.detail?.message || err?.message || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  // RC 1.48.3 — soft reject-gate handlers. "Generate Anyway" retries
  // the same Generate flow with `force_generate: true`; "Cancel"
  // simply dismisses the warning and leaves the wizard idle so the
  // user can pick a different CV / role / context.
  const handleRejectWarnConfirm = async () => {
    if (!rejectWarnModal) return;
    const captured = rejectWarnModal;
    setRejectWarnModal(null);
    setGenerating(true);
    try {
      const result = await _runGenerateInner(captured.opts, null, true);
      const _cvCost = getActionCost(credits.usageCosts, 'cv_improve');
      const _cvLabel = _cvCost == null ? 'Credits' : `${_cvCost} ${_cvCost === 1 ? 'Credit' : 'Credits'}`;
      _applyGenerateSuccess(result, _cvLabel);
    } catch (err) {
      if (handleCreditsError(err, { action: 'Generate CV', onExhausted: onUsageExhaustedFromBackend, refreshCredits: credits?.refresh })) return;
      const friendly = humaniseWizardError(err, 'cv');
      toast.error(friendly || err?.data?.detail?.message || err?.message || 'Generation failed.');
    } finally { setGenerating(false); }
  };

  const handleRejectWarnCancel = () => {
    setRejectWarnModal(null);
  };

  // RC 1.48.6 — High-CV Value Guard modal handlers.
  //
  //   "Generate anyway"  → persist per-job session confirmation so
  //                        the user is not re-prompted this session,
  //                        then resume the captured generate flow
  //                        with `highCvConfirmed=true` (audit-only
  //                        backend signal).
  //   "Use current CV"   → dismiss; user keeps Source CV. No
  //                        sessionStorage write — next click will
  //                        re-prompt until confirmation.
  const handleHighCvConfirm = async () => {
    if (!highCvWarnModal) return;
    const captured = highCvWarnModal;
    try {
      sessionStorage.setItem(highCvSessionKey(jobId), '1');
    } catch { /* sessionStorage unavailable — proceed anyway */ }
    setHighCvWarnModal(null);
    await _generateVariantAfterHighCvCheck(captured.opts, true);
  };

  const handleHighCvCancel = () => {
    setHighCvWarnModal(null);
  };

  // Shared post-generate success path. Reused by the normal flow,
  // refresh-then-generate, and decline-then-generate paths.
  const _applyGenerateSuccess = ({ variant, variantId, charge }, _cvLabel) => {
    const result = {
      variant_id: variantId,
      variant_name: variant.variant_name,
      ...charge,
    };
    setGeneratedResult(result);
    // RC 1.49.x — clear the in-flight CV marker. The variant is now
    // bound to wizard state; a remount will hydrate from
    // `wizardState:{userId}:{jobId}` directly.
    clearInflightCvMarker(primaryCv?.user_id, jobId);
    setSelectedCv({
      type: 'cv_variant',
      id: variantId,
      score: charge.cv_sub_score_variant ?? null,
      name: variant.variant_name,
      label: 'Tailored Variant',
    });
    // RC 1.47.1 — qualitative confidence-band copy; RC 1.47.2 layers
    // a stale-baseline disclaimer on top when the user declined the
    // free refresh.
    if (charge.charged) {
      if (charge.reason === 'cv_quality_improved') {
        toast.success(`Material improvement achieved — ${_cvLabel} charged (Δ +${charge.delta ?? '?'} pts vs Source CV).`);
      } else if (charge.reason === 'presentation_optimisation_high_cv') {
        // RC 1.48.6 — High-CV Value Guard: Source CV was already ≥80
        // and the regenerated variant did not materially improve
        // (Δ < +6). Backend charges 50% of the standard `cv_improve`
        // price. Surface the charge percentage so the user can
        // reconcile their Credits balance against the audit ledger.
        const _pct = charge.charge_percentage ?? 50;
        toast.info(`Presentation optimisation applied — ${_pct}% of ${_cvLabel} charged (Source CV already ≥80; Δ ${(charge.delta ?? 0) >= 0 ? '+' : ''}${charge.delta ?? 0} pts).`);
      } else if (charge.reason === 'no_baseline_protection') {
        if (charge.stale_baseline_ignored) {
          toast.info(`${_cvLabel} charged — prior baseline was outdated and was not used for this comparison.`);
        } else {
          toast.success(`Tailoring complete — ${_cvLabel} charged (no Analyse Fit baseline existed).`);
        }
      } else {
        toast.success(`Tailoring complete — ${_cvLabel} charged.`);
      }
    } else if (charge.reason === 'within_tolerance_band') {
      toast.success(`Within NDOR confidence band — no charge (Δ ${(charge.delta ?? 0) >= 0 ? '+' : ''}${charge.delta ?? 0} pts, ±${charge.tolerance_band ?? 5}).`);
    } else if (charge.reason === 'cv_quality_no_improvement') {
      toast.success('No material uplift detected — generated CV did not exceed your Source CV. No Credits charged.');
    } else {
      toast.success('Tailoring complete — no charge.');
    }
    track('tailored_cv_generated', {
      charged: !!charge.charged,
      reason: charge.reason || null,
      baseline_status: charge.baseline_status || null,
      stale_baseline_ignored: !!charge.stale_baseline_ignored,
    });
    fireFirstActionOnce('tailored_cv');
    setGenerateSheetOpen(false);
    credits?.refresh?.();
    // RC 1.52 Phase 3A.4 — start the 5-min Generate-CV cooldown so
    // the wizard's Generate CV CTA can't be re-clicked on a stale
    // balance. Mirrors the workspace CvSection behaviour.
    try {
      startCooldown(cvVariantCooldownKey(primaryCv?.user_id, jobId));
    } catch { /* noop */ }
    // RC 1.49.4 Tier 1 — variant list mutated; drop the cache so
    // ApplicationsPage / DocumentsPage / Pack pages see the fresh
    // row on next mount. The bare `cv-variants:all` key is shared.
    cacheInvalidate(['cv-variants:all', 'jobs', 'dashboard:bundle']);
    // RC 1.48.6 — the new variant adds a row to the score landscape.
    // Refresh the wizard carry-forward so Step 3's Score Landscape
    // shows the fresh ladder synchronously on advance.
    refreshFitSnapshot();
  };

  const generateLetter = async () => {
    if (!job?.description?.trim()) { toast.error('Job has no description text.'); return; }
    if (!ensureCreditsAvailable('cover_letter', 'Generate cover letter')) return;
    setLetterGenerating(true);
    try {
      const variantIdForCv = selectedCv?.type === 'cv_variant' ? selectedCv.id : null;
      const letterTitleNew = mintCoverLetterTitle({ company: job?.company });
      // RC 1.49.1 P0-FIN (2026-05-08) — financial idempotency for
      // cover letter retries. Action-scoped key so a CV-variant key
      // for the same (user, job) never collides.
      const letterIdempotencyKey = getOrMintIdempotencyKey(
        primaryCv?.user_id, jobId, 'cover_letter',
      );
      // RC 1.49.5 L-2 (operator-locked, 2026-05-08) — write the
      // remount-rebind marker BEFORE the apiFetch so a wizard
      // unmount during the long LLM call (or a tab background)
      // can be recovered on remount. Cleared on confirmed success
      // (line ~2625), or by the bootstrap branch if the letter is
      // already persisted server-side.
      writeInflightLetterMarker(primaryCv?.user_id, jobId, letterTitleNew);
      // Real backend signature: requires job_text + title; tone_type ∈
      // {analytical, strategic, commercial}.
      // RC 1.49.1 P0-A — wrap so a 30s ingress 504 doesn't double-charge.
      const r = await withTimeoutRecovery({
        kind:  'letter',
        jobId,
        onRecoveryState: (s) => {
          if (s === 'checking') {
            toast.info("This is taking longer than usual. We're checking your results...");
          } else if (s === 'recovered') {
            toast.success('Your result was completed successfully.');
          } else if (s === 'failed') {
            toast.warning(
              "We're unable to confirm the result reached you. Before clicking Generate again, please open Documents → Cover Letters — your document may already be there.",
              { duration: 9000 },
            );
          }
        },
        generate: () => apiFetch('/api/cover-letters/generate', {
          method: 'POST',
          body: {
            job_text: job.description,
            title: letterTitleNew,
            base_cv_id: primaryCv?.id,
            cv_variant_id: variantIdForCv,
            tone_type: 'analytical',
            // RC 1.49.1 P0-FIN — financial idempotency for retries.
            idempotency_key: letterIdempotencyKey,
          },
        }),
      });
      const newLetterId = r?.cover_letter_id || r?.id;
      const newLetterContent = r?.generated_text || r?.content_markdown || '';
      setLetterId(newLetterId);
      setLetterDraft(newLetterContent);
      setLetterSavedDraft(newLetterContent);
      setLetterTitle(r?.title || letterTitleNew);
      // RC 1.49.1 P0-FIN — clear cached key on confirmed success.
      clearIdempotencyKey(primaryCv?.user_id, jobId, 'cover_letter');
      // RC 1.49.5 L-2 — clear remount-rebind marker on success.
      clearInflightLetterMarker(primaryCv?.user_id, jobId);
      // RC 1.49.x.RUN_ISOLATION — letterReused is permanently false.
      // RC 1.49.4 Tier 1 — letter list mutated; drop cache so
      // ApplicationsPage / Pack pages see the fresh letter.
      cacheInvalidate(['cover-letters:all', 'dashboard:bundle']);
      toast.success('Cover letter generated.');
      track('cover_letter_generated');
      fireFirstActionOnce('cover_letter');
      credits?.refresh?.();
    } catch (err) {
      if (handleCreditsError(err, { action: 'Generate cover letter', onExhausted: onUsageExhaustedFromBackend, refreshCredits: credits?.refresh })) return;
      // RC 1.49.x Slice 4 — humanise generic 5xx for the letter flow.
      const friendly = humaniseWizardError(err, 'letter');
      toast.error(friendly || err?.data?.detail?.message || err?.message || 'Cover letter generation failed.');
    } finally { setLetterGenerating(false); }
  };

  const saveLetterIfDirty = useCallback(async () => {
    if (!letterId || letterDraft === letterSavedDraft) return;
    try {
      await apiFetch(`/api/cover-letters/${letterId}`, {
        method: 'PATCH', body: { content_markdown: letterDraft },
      });
      setLetterSavedDraft(letterDraft);
    } catch { /* non-fatal — Step 4 will still show the latest local draft */ }
  }, [letterId, letterDraft, letterSavedDraft]);

  const advanceStep = useCallback(async () => {
    // RC 1.49.2 — save letter on transition Letter → Fit (stepIdx
    // 1 → 2) so the user has a clean draft pinned before running
    // Analyse Fit. The letter is intentionally NOT included in the
    // fit score (apples-to-apples with the Source-CV baseline) but
    // we still want their edits durably saved before the next click.
    if (stepIdx === 1) await saveLetterIfDirty();
    setStepIdx(i => Math.min(i + 1, STEPS.length - 1));
  }, [stepIdx, saveLetterIfDirty]);

  const skipLetter = () => {
    setLetterId(null); setLetterDraft(''); setLetterTitle('');
    // Skip jumps directly to the Analyse Fit step (index 2).
    setStepIdx(2);
  };

  // RC 1.49.x — Final Pack Confirmation gate.
  //   • `requestCreatePack` runs the existing pre-flight (selection
  //     present + Credits available) and, if those pass, opens the
  //     blocking confirmation modal. The actual POST is deferred to
  //     `confirmCreatePack` below.
  //   • This wrapper preserves all upstream logic (Step3 binding,
  //     pack snapshot assembly, 409 handling) — the modal is purely
  //     a UI gate.
  const requestCreatePack = () => {
    if (!selectedCv?.id || !primaryCv?.id) return;
    if (!ensureCreditsAvailable('application_pack', 'Create application pack')) return;
    setConfirmCreateOpen(true);
  };
  const confirmCreatePack = () => {
    setConfirmCreateOpen(false);
    createPack();
  };
  // Composition signals fed to the modal — single source of truth
  // for "what's in the pack" so the modal cannot drift from the
  // export builder's gate predicates.
  const finalPackComposition = {
    hasTailoredCv: selectedCv?.type === 'cv_variant',
    hasCoverLetter: !!letterId,
    hasVariantFit: !!(
      selectedCv?.type === 'cv_variant'
      && step3LastResult?.sourceType === 'cv_variant'
      && step3LastResult?.sourceId === selectedCv.id
    ),
    hasBaselineAnchor: !!primaryCv?.id,
  };
  const createPack = async () => {
    if (!selectedCv?.id || !primaryCv?.id) return;
    if (!ensureCreditsAvailable('application_pack', 'Create application pack')) return;
    setCreating(true);
    try {
      // RC 1.49.x.RUN_ISOLATION (operator-locked, 2026-05-06) —
      // Pack binds STRICTLY to the analyse_fit row produced by the
      // EXPLICIT Step 3 run on the CURRENTLY SELECTED CV. If the
      // user changed their selected CV AFTER running Analyse Fit
      // (e.g. ran fit on Variant A, then selected Variant B), the
      // captured row no longer matches the bound CV — drop it.
      //
      // RC 1.49.x.TRANSIENT_SCORING Stage 2 (2026-05-06) — split
      // the predicate into two:
      //   • `step3SelectionMatchesSelected` — sourceType + sourceId
      //     match selected CV. True for both variant transient
      //     runs (no scoreId) and Source-CV runs (with scoreId).
      //     This drives `pack_snapshot.fit` population.
      //   • `step3HasPersistentScoreId` — the captured run has a
      //     real `job_scores` row id (Source CV runs only after
      //     Stage 2; variant runs always null). This drives the
      //     forensic `analyse_fit_id` binding.
      const step3SelectionMatchesSelected = (
        step3LastResult?.sourceType
        && step3LastResult?.sourceId
        && (
          (step3LastResult.sourceType === 'cv_variant'
            && selectedCv.type === 'cv_variant'
            && step3LastResult.sourceId === selectedCv.id)
          || (step3LastResult.sourceType === 'uploaded_cv'
            && selectedCv.type === 'base_cv'
            && step3LastResult.sourceId === selectedCv.id)
        )
      );
      const step3HasPersistentScoreId = !!(
        step3SelectionMatchesSelected && step3LastResult?.scoreId
      );
      let analyseFitIdToBind = step3HasPersistentScoreId
        ? step3LastResult.scoreId
        : null;

      // RC 1.49.x.PACK_BINDING_DIAGNOSTIC Fix B (operator-locked,
      // 2026-05-06) — Source-CV-only packs may bind to the
      // *current* canonical Source CV baseline (the row returned
      // by `/api/jobs/{id}/baseline-fit`). This is NOT a
      // historical fallback: `/baseline-fit` is the deterministic,
      // current-primary-CV baseline already displayed on Step 1
      // (`Source CV NDOR fit X/100`), and the resolver still binds
      // STRICTLY to a single explicit `analyse_fit_id` — no
      // walking, no "newest matching", no `fallback_last_analysis`.
      // Eligibility is gated by:
      //   • selectedCv.type === 'base_cv' (Source-CV-only path)
      //   • no Step 3 fit captured this run
      //   • baseline-fit endpoint returned a `score_id`
      //   • baseline's `primary_cv.id` is still the user's current
      //     Source CV (so a baseline against a swapped-out CV is
      //     never bound).
      const canBindBaseline = (
        analyseFitIdToBind == null
        && selectedCv.type === 'base_cv'
        && baselineFit?.baseline?.score_id
        && baselineFit?.primary_cv?.id
        && primaryCv?.id
        && baselineFit.primary_cv.id === primaryCv.id
      );
      if (canBindBaseline) {
        analyseFitIdToBind = baselineFit.baseline.score_id;
      }

      // ── RC 1.49.x.TRANSIENT_SCORING Stage 1+2 (2026-05-06) ─────
      // Build the self-contained pack snapshot. The pack page
      // reads canonical fit, sub-scores, CV verdict, and source
      // baseline EXCLUSIVELY from this object — never from
      // job_scores. Three sources contribute:
      //   1. step3LastResult.scoreRow when the Step 3 selection
      //      matches the selected CV. Stage 2: this branch fires
      //      for variant transient runs WITHOUT requiring a DB
      //      scoreId (the run never persists to job_scores).
      //   2. baselineFit.baseline (current canonical Source-CV
      //      baseline; used when Source CV is selected and Step 3
      //      was skipped — Fix B path).
      //   3. baselineFit.baseline as `source_baseline` regardless,
      //      so the Pack page can render the Initial Fit card for
      //      tailored packs without cross-pack queries.
      let snapshotFit = null;
      if (step3SelectionMatchesSelected && step3LastResult?.scoreRow) {
        snapshotFit = step3LastResult.scoreRow;
      } else if (canBindBaseline) {
        const b = baselineFit.baseline;
        snapshotFit = {
          score_id:        b.score_id ?? null,
          fit_score:       b.fit_score ?? null,
          final_decision:  b.final_decision ?? null,
          salary_score:    b.salary_score ?? b.sub_scores?.salary ?? null,
          seniority_score: b.seniority_score ?? b.sub_scores?.seniority ?? null,
          location_score:  b.location_score ?? b.sub_scores?.location ?? null,
          strategic_score: b.strategic_score ?? b.sub_scores?.strategic ?? null,
          risk_score:      b.risk_score ?? b.sub_scores?.risk ?? null,
          evaluated_at:    b.evaluated_at ?? null,
          reasoning_json:  b.reasoning_json ?? b.reasoning ?? {},
        };
      }

      const cvVerdictForSnapshot = (() => {
        // Tailored variant verdict comes from the variant card
        // (cv_sub_score_variant from finalize-charge). Source CV
        // verdict comes from baseline reasoning (`cv_sub_score`).
        if (selectedCv.type === 'cv_variant') {
          const cvSub = generatedResult?.cv_sub_score_variant;
          if (cvSub != null) {
            return {
              cv_sub_score: cvSub,
              cv_sub_score_provisional: !!generatedResult?.cv_sub_score_provisional,
            };
          }
        }
        if (selectedCv.type === 'base_cv') {
          const cvSub = baselineFit?.baseline?.reasoning_json?.cv_sub_score
                     ?? baselineFit?.baseline?.cv_sub_score
                     ?? null;
          if (cvSub != null) {
            return { cv_sub_score: cvSub, cv_sub_score_provisional: false };
          }
        }
        return null;
      })();

      const sourceBaselineForSnapshot = (
        baselineFit?.baseline?.score_id
        && baselineFit?.primary_cv?.id === primaryCv?.id
      ) ? {
        score_id:     baselineFit.baseline.score_id,
        fit_score:    baselineFit.baseline.fit_score ?? null,
        evaluated_at: baselineFit.baseline.evaluated_at ?? null,
        // U-14.B (2026-02-15) — additive `cv_sub_score` so the
        // Applications list chip can render the Source CV's CV
        // SUB-SCORE (not the overall fit_score). Backend already
        // surfaces this at pack-detail-read time via
        // `application_packs.py` L1469-1497 (alternatives[].cv_sub_score);
        // the list endpoint, however, only ships `pack_snapshot.*`
        // — so the snapshot must carry the sub-score itself. The
        // helper `computeChipDisplay` treats `null` as 'legacy' and
        // renders "—" rather than misleading the user with the
        // overall fit_score (operator-locked: never fall back to
        // overall fit). No backend migration; legacy snapshots
        // ship `cv_sub_score: undefined` → chip degrades to "—".
        cv_sub_score: (
          baselineFit?.baseline?.reasoning_json?.cv_sub_score
          ?? baselineFit?.baseline?.cv_sub_score
          ?? null
        ),
        cv_sub_score_provisional: !!(
          baselineFit?.baseline?.reasoning_json?.cv_sub_score_provisional
          ?? baselineFit?.baseline?.cv_sub_score_provisional
        ),
      } : null;

      const packSnapshot = {
        version:     1,
        captured_at: new Date().toISOString(),
        binding: {
          cv_kind: selectedCv.type === 'cv_variant' ? 'tailored_variant' : 'source_cv',
          cv_variant_id: selectedCv.type === 'cv_variant' ? selectedCv.id : null,
          base_cv_id:    primaryCv.id,
          cv_label:      selectedCv.label || null,
        },
        fit:             snapshotFit,
        cv_verdict:      cvVerdictForSnapshot,
        source_baseline: sourceBaselineForSnapshot,
      };

      const body = {
        job_id: jobId,
        base_cv_id: primaryCv.id,
        cv_variant_id: selectedCv.type === 'cv_variant' ? selectedCv.id : null,
        cover_letter_id: letterId || null,
        analyse_fit_id: analyseFitIdToBind,
        pack_status: 'draft',
        pack_name: packName.trim(),
        notes,
        // RC 1.49.x.TRANSIENT_SCORING Stage 1 (operator-locked, 2026-05-06)
        // — always send the assembled snapshot. The pack page reads
        // canonical fit, sub-scores, CV verdict, and source baseline
        // EXCLUSIVELY from this column. Source-CV-only packs and
        // packs without Analyse Fit still send the snapshot (binding +
        // source_baseline / cv_verdict when available, fit=null).
        pack_snapshot: packSnapshot,
      };
      const pack = await apiFetch('/api/application-packs', { method: 'POST', body });
      clearWizardState(jobId);
      onOpenChange(false);
      onPackCreated?.(pack);
      toast.success('Application pack created.');
      track('application_pack_generated', {
        has_cover_letter: !!letterId,
        used_variant: selectedCv.type === 'cv_variant',
      });
      fireFirstActionOnce('application_pack');
      // RC 1.49.4 Tier 1 — pack mutation invalidates list caches
      // so the new pack appears immediately on /applications and
      // the Jobs page badge counts are refreshed on next visit.
      cacheInvalidate(['applications', 'application-packs:all', 'jobs', 'dashboard:bundle']);
      // RC 1.49.3 P1-3 — post-creation feedback. Redirect to the
      // applications LIST (not detail page) with the new pack id
      // in the query string so ApplicationsPage can auto-scroll
      // and pulse-highlight the freshly-created row.
      navigate(`/applications?highlight=${encodeURIComponent(pack.id)}`);
    } catch (err) {
      if (handleCreditsError(err, { action: 'Create application pack', onExhausted: onUsageExhaustedFromBackend, refreshCredits: credits?.refresh })) return;
      // ── RC 1.49.x.STAGE_3.JOB_LIFECYCLE (operator-locked, 2026-05-06) ──
      // 409 duplicate-active-pack guard. Surface the existing pack to
      // the user and offer a single click through to it — no re-charge,
      // no duplicate clutter on /applications.
      const status = err?.status || err?.response?.status;
      const det = err?.data?.detail;
      const isDup = status === 409 && det && typeof det === 'object'
                    && det.error === 'duplicate_active_pack'
                    && det.existing_pack_id;
      if (isDup) {
        const existingId = det.existing_pack_id;
        toast.info(det.message || 'You already have an active pack for this job.', {
          action: {
            label: 'Open existing pack',
            onClick: () => navigate(`/applications/${existingId}`),
          },
        });
        clearWizardState(jobId);
        onOpenChange(false);
        return;
      }
      toast.error(err?.data?.detail?.message || (humaniseWizardError(err, 'pack') || err?.message) || 'Failed to create pack.');
    } finally { setCreating(false); }
  };

  const openCvPreview = async () => {
    // RC 1.49.x SOURCE_CV_NEVER_PREVIEWABLE — only Tailored CV
    // variants are previewable. If the selected card is the user's
    // Source CV, the helper is a no-op.
    if (!selectedCv || selectedCv.type !== 'cv_variant') return;
    setPreviewLoading(true);
    setPreviewOpen({ title: selectedCv?.name || 'Variant', mode: 'variant', data: null });
    try {
      const v = await apiFetch(`/api/cv-variants/${selectedCv.id}`);
      setPreviewOpen({ title: v.variant_name || 'Variant', mode: 'variant', data: v });
    } catch (err) {
      setPreviewOpen({ title: 'Preview unavailable', mode: 'variant', data: { content_markdown: err?.message || '' } });
    } finally { setPreviewLoading(false); }
  };

  // RC 1.49.x SOURCE_CV_NEVER_PREVIEWABLE — `previewBaseCv` retired
  // globally. Only Tailored CV variants are previewable.
  const previewVariant = async (id, nameHint) => {
    if (!id) return;
    setPreviewLoading(true);
    setPreviewOpen({ title: nameHint || 'Variant', mode: 'variant', data: null });
    try {
      const v = await apiFetch(`/api/cv-variants/${id}`);
      setPreviewOpen({ title: v.variant_name || nameHint || 'Variant', mode: 'variant', data: v });
    } catch (err) {
      setPreviewOpen({ title: 'Preview unavailable', mode: 'variant', data: { content_markdown: err?.message || '' } });
    } finally { setPreviewLoading(false); }
  };

  const openLetterPreview = () => {
    setPreviewOpen({
      title: letterTitle || 'Cover letter',
      mode: 'letter',
      data: { content_markdown: letterDraft || '' },
    });
  };

  // RC 1.49.4 L-4 (operator-locked, 2026-05-08) — cancel-warning
  // when work is in flight. Guards Escape, backdrop click, and the
  // explicit close button. Browser-level beforeunload also fires
  // while in-flight so a tab close / hard refresh surfaces the
  // native "Leave site?" prompt. Cleared the moment all flags
  // settle, so the warning is never sticky.
  const [showCancelWarn, setShowCancelWarn] = useState(false);
  const hasInflightWork =
    !!generating || !!letterGenerating || !!step3FitRunning || !!creating;
  const guardedOpenChange = (next) => {
    if (!next && hasInflightWork) {
      setShowCancelWarn(true);
      return;                       // suppress close until the user confirms
    }
    onOpenChange(next);
  };
  useEffect(() => {
    if (!open || !hasInflightWork) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';           // legacy spec; required for Chrome to surface the prompt
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [open, hasInflightWork]);

  return (
    <>
      <Dialog open={open} onOpenChange={guardedOpenChange}>
        <DialogContent
          className="max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6"
          data-testid="job-application-wizard"
        >
          <DialogHeader className="space-y-2 sm:space-y-3 pb-2 sm:pb-3">
            <DialogTitle className="text-base sm:text-lg">Apply to this job</DialogTitle>
            <DialogDescription className="sr-only">
              Forward-only 4-step application flow: choose or generate a tailored CV
              (or use your Source CV), optionally generate a cover letter, optionally
              run Variant Fit on a tailored CV, then review and create the
              application pack. Each step is optional except CV selection.
              You remain in control — NDOR never auto-applies on your behalf.
            </DialogDescription>
            <StepIndicator stepIdx={stepIdx} />
          </DialogHeader>
          {stepIdx === 0 && (
            <Step2Cv
              job={job} primaryCv={primaryCv}
              sourceFitNdor={fitScore}
              sourceCvVerdict={baselineFit?.baseline?.sub_scores?.cv_sub_score ?? null}
              hasBaselineFit={fitScore != null}
              jobId={jobId}
              selectedCv={selectedCv} onSelectCv={setSelectedCv}
              generating={generating} onGenerate={openGenerateSheet}
              progressMessage={cvProgressMessage}
              creditsLoading={creditsLoading}
              generatedResult={generatedResult}
              onPreviewVariant={(id, name) => previewVariant(id, name)}
              onBack={() => onOpenChange(false)} onContinue={advanceStep}
            />
          )}
          {stepIdx === 1 && (
            <Step3Letter
              letterDraft={letterDraft} setLetterDraft={setLetterDraft}
              generating={letterGenerating} onGenerate={generateLetter}
              progressMessage={letterProgressMessage}
              creditsLoading={creditsLoading}
              hasLetter={!!letterId}
              onSkip={skipLetter}
              onBack={() => setStepIdx(0)} onContinue={advanceStep}
            />
          )}
          {stepIdx === 2 && (
            <Step3AnalyseFit
              jobId={jobId}
              selectedCv={selectedCv}
              onAnalyse={runAnalyseFitOnSelected}
              analysing={step3FitRunning}
              bestFit={bestFit}
              lastResult={step3LastResult}
              baselineFit={baselineFit}
              creditsLoading={creditsLoading}
              userId={primaryCv?.user_id}
              onBack={() => setStepIdx(1)} onContinue={advanceStep}
            />
          )}
          {stepIdx === 3 && (
            <Step4Review
              job={job} primaryCv={primaryCv}
              fitScore={fitScore} fitDate={fitDate} selectedCv={selectedCv}
              generatedResult={generatedResult} letterId={letterId}
              letterDraft={letterDraft} letterTitle={letterTitle}
              packName={packName} setPackName={setPackName}
              notes={notes} setNotes={setNotes}
              creating={creating} onBack={() => setStepIdx(2)} onCreate={requestCreatePack}
              progressMessage={packProgressMessage}
              creditsLoading={creditsLoading}
              onPreviewCv={openCvPreview} onPreviewLetter={openLetterPreview}
              bestFit={bestFit}
              onBackToFitStep={() => setStepIdx(2)}
            />
          )}
        </DialogContent>
      </Dialog>

      <PreviewModal
        open={!!previewOpen}
        onClose={() => setPreviewOpen(null)}
        title={previewOpen?.title}
        mode={previewOpen?.mode}
        data={previewOpen?.data}
        loading={previewLoading}
      />

      {/* RC 1.41.G4.F — Insufficient-Credits modal. Opened by either
          the client-side pre-flight check (`ensureCreditsAvailable`)
          or by the catch-block routing (`handleCreditsError` →
          `onUsageExhaustedFromBackend`). The modal reads the
          plan_code, action_type, and required/included/extra
          numbers from the structured detail body, so it works
          identically whether the gate fires client-side or
          server-side. */}
      <UsageExhaustedModal
        open={usageExhaustedOpen}
        onClose={() => setUsageExhaustedOpen(false)}
        planCode={usageExhaustedDetail?.plan_code || credits?.planCode || null}
        action={usageExhaustedDetail?.action_type || null}
        required={usageExhaustedDetail?.required_usage ?? null}
        availableIncluded={usageExhaustedDetail?.included_usage_remaining ?? null}
        availableExtra={usageExhaustedDetail?.extra_usage_remaining ?? null}
      />

      {/* RC 1.44 P0.4 — Inferior-source / inferior-variant gate.
          Backend returns HTTP 409 with `code` =
          'INFERIOR_SOURCE' | 'INFERIOR_VARIANT' before any usage
          is debited; the modal surfaces the four canonical
          options. `Continue anyway` re-runs with
          force_override=true. */}
      <InferiorSourceModal
        open={Boolean(inferiorModal)}
        code={inferiorModal?.code || null}
        guidance={inferiorModal?.guidance || {}}
        onClose={() => setInferiorModal(null)}
        onStayWithSourceCv={handleStayWithSourceCv}
        onChooseStrongerCv={handleChooseStrongerCv}
        onGenerateNewCv={handleGenerateNewCv}
        onContinueAnyway={handleContinueAnyway}
        busyAction={inferiorBusy}
      />

      {/* RC 1.41.G4.G — Pre-generation configuration sheet. Opened by
          Step 2's "Generate" button via `openGenerateSheet`. The
          actual /api/cv-variants/generate call only fires on
          Confirm → onConfirm(opts) → generateVariant(opts). Cancel
          closes without firing anything. */}
      <GenerateCvOptionsSheet
        open={generateSheetOpen}
        onOpenChange={setGenerateSheetOpen}
        onConfirm={generateVariant}
        generating={generating}
      />

      {/* RC 1.49.x — Final Pack Confirmation. Blocks POST
          /api/application-packs until the user explicitly confirms
          the pack composition. UI-only gate; does NOT change any
          downstream pack-creation logic. */}
      <FinalPackConfirmModal
        open={confirmCreateOpen}
        onOpenChange={(v) => { if (!creating) setConfirmCreateOpen(v); }}
        composition={finalPackComposition}
        creating={creating}
        onCancel={() => setConfirmCreateOpen(false)}
        onConfirm={confirmCreatePack}
      />

      {/* RC 1.47.2 — Version-Aware Baseline Governance modal. Fires
          whenever the canonical baseline for this job is stale and
          the user is about to incur a (potentially unfair) charge.
          Operator UX-A: re-fires every time, never persisted across
          sessions, never silently bypassed. */}
      <BaselineRefreshModal
        open={Boolean(baselineModal)}
        onOpenChange={(v) => !v && setBaselineModal(null)}
        baselineVersion={baselineModal?.baseline_version}
        currentVersion={baselineModal?.current_version}
        onRefresh={handleBaselineRefresh}
        onDecline={handleBaselineDecline}
        onCancel={() => setBaselineModal(null)}
      />

      {/* RC 1.48.3 (2026-05-04) — Soft reject-gate warning. Fires
          ONLY when the backend returns 409 `analyse_fit_reject`,
          which post-RC-1.48.3 is reachable solely via a literal
          `final_decision === 'reject'` from the latest Source CV
          analyse-fit row (the legacy fit_score < 60 trigger is
          retired). Default UX is "Generate Anyway" — operator
          directive: governance must be constructive, not blunt-
          reject. The pack-canonical pre-emptive bypass means jobs
          with an existing pack never reach this modal. */}
      <Dialog
        open={Boolean(rejectWarnModal)}
        onOpenChange={(v) => !v && setRejectWarnModal(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="wizard-reject-warn-modal"
        >
          <DialogHeader>
            <DialogTitle data-testid="wizard-reject-warn-title">
              Low fit detected — generate anyway?
            </DialogTitle>
            <DialogDescription
              data-testid="wizard-reject-warn-description"
              className="text-sm text-ndor-ink-secondary"
            >
              Your latest Source CV Analyse Fit for this role landed
              {rejectWarnModal?.fit_score != null ? (
                <> at <span className="font-mono-ui tabular-nums">{rejectWarnModal.fit_score}/100</span></>
              ) : (
                <> in the low-fit band</>
              )}
              {' '}with a verdict of{' '}
              <span className="font-medium">
                {rejectWarnModal?.final_decision || 'reject'}
              </span>
              . NDORFlow governance treats this as a directional signal
              — not a hard block. You can still generate a tailored CV
              if you have additional context to add or simply want to
              proceed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={handleRejectWarnCancel}
              disabled={generating}
              data-testid="wizard-reject-warn-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectWarnConfirm}
              disabled={generating}
              data-testid="wizard-reject-warn-confirm"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1.5" />
              )}
              Generate Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RC 1.48.6 (2026-05-04) — High-CV Value Guard warning dialog.
          Fires BEFORE the generate call when the Source CV NDOR
          verdict for this job is already ≥80/100. Two CTAs:
          "Use current CV" (cancel) and "Generate anyway" (resume
          flow, persist per-job session confirmation, forward
          `high_cv_generate_confirmed=true` to backend for audit).
          Commercial transparency: warns the user that further
          optimisation is likely presentation-only — the backend
          charges 50% (presentation optimisation) when
          variant − source < +6, or 100% on a ≥+6 material lift. The
          authoritative gate remains server-side.
          RC 1.48.7A (2026-05-04) — candidate-facing copy cleaned of
          internal-cost language ("LLM cost"). Credits are the only
          monetary unit surfaced to candidates. */}
      <Dialog
        open={Boolean(highCvWarnModal)}
        onOpenChange={(v) => !v && setHighCvWarnModal(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="wizard-high-cv-warn-modal"
        >
          <DialogHeader>
            <DialogTitle data-testid="wizard-high-cv-warn-title">
              Your Source CV is already strong
            </DialogTitle>
            <DialogDescription
              data-testid="wizard-high-cv-warn-description"
              className="text-sm text-ndor-ink-secondary"
            >
              Your current Source CV scores{' '}
              <span
                className="font-mono-ui tabular-nums"
                data-testid="wizard-high-cv-warn-score"
              >
                {highCvWarnModal?.source_cv_score ?? ''}/100
              </span>
              {' '}on this role. Further optimisation may mainly
              improve presentation and clarity rather than your
              underlying fit.
              {' '}<span className="font-medium">If you continue,
              this will use Credits — 50% of the standard tailoring
              cost for a presentation-only refinement, or the full
              cost when the new variant beats your Source CV by 6
              points or more.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={handleHighCvCancel}
              disabled={generating}
              data-testid="wizard-high-cv-warn-cancel"
            >
              Use current CV
            </Button>
            <Button
              onClick={handleHighCvConfirm}
              disabled={generating}
              data-testid="wizard-high-cv-warn-confirm"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1.5" />
              )}
              Generate anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RC 1.49.4 L-4 (operator-locked, 2026-05-08) — cancel
          warning. Surfaced when the user attempts to close while
          CV generation, cover-letter generation, Analyse Fit, or
          pack creation is in flight. "Stay" returns to the wizard
          (no state change). "Close anyway" closes the wizard via
          the original `onOpenChange(false)` — backend work is
          NOT cancelled (per operator-locked copy), but
          idempotency keys + the inflight CV marker still protect
          financial correctness. */}
      <Dialog
        open={showCancelWarn}
        onOpenChange={(v) => !v && setShowCancelWarn(false)}
      >
        <DialogContent
          className="max-w-md"
          data-testid="wizard-cancel-warn-dialog"
        >
          <DialogHeader>
            <DialogTitle data-testid="wizard-cancel-warn-title">
              Work in progress
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              <span className="block mb-2 text-foreground">
                If you close this wizard now:
              </span>
              <ul className="list-disc pl-5 space-y-1 text-foreground">
                <li>Completed steps may already be charged.</li>
                <li>In-flight generation may still complete and be charged.</li>
                <li>Generated documents may appear in Documents/CV Variants.</li>
                <li>Cancelling closes the wizard UI but may not cancel backend work.</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowCancelWarn(false)}
              data-testid="wizard-cancel-warn-stay"
            >
              Stay
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowCancelWarn(false);
                onOpenChange(false);
              }}
              data-testid="wizard-cancel-warn-close-anyway"
            >
              Close anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
